/**
 * Discount Note Synchronization Module (v10)
 * Ripple Tea / T-Soft ERP integration
 *
 * Writes discount data into two places on the order:
 *
 *   1. cart.note        - human-readable Discount Summary
 *   2. cart.attributes  - automatic offers:  _offer_data, _offer_total
 *                         discount codes:    _discount_code,
 *                                            _discount_code_data,
 *                                            _discount_code_total
 *
 * ONLY products that actually received a discount are included.
 *
 * Reconciliation for the whole order:
 *   _offer_total + _discount_code_total == order.total_discounts
 *   SUM(paid_quantity x price)          == order.total_price   (offers only)
 *
 * UNCHANGED BY THIS MODULE: discount calculation, pricing, checkout.
 *
 * =============================================================================
 * FIXED IN v10.5.3 - same SKU on two cart lines (order #1114864)
 *
 * An offer that covers only part of a product's quantity ("Buy 4" with 5 in
 * the cart) leaves the cart with two lines of one variant: the discounted
 * units and the rest at full price. _offer_data listed only the discounted
 * line (Qty 4, Paid 3) while the order merged them into one line of 5, so
 * T-Soft came out short by the full-price units. Entries are now added
 * together per SKU - see mergePayloadBySku(). Discount amounts, free units,
 * the note layout and the attribute names are all unchanged.
 * =============================================================================
 *
 * =============================================================================
 * FIXED IN v10.5 - orders arriving with no offer data, or with stale data
 *
 * Three orders on 15 Oct 2026 were reported as wrong. They were three
 * different things:
 *
 *   #1112733  NOT A BUG. 8 units at 120 under DS_FLAVOUREDTEA_B4P3_120
 *             (120 off per 4) gives 240 off and 2 free units. The cart held
 *             four lines of quantity 2 - four identical claims on 2 free
 *             units - so only two lines can carry one each; a line cannot
 *             hold half a free unit. Billed 6 x 120 = 720 = order total, and
 *             free x price = 240 = order discount. Both reconcile exactly.
 *             The maths is unchanged in this version.
 *
 *   #1112701  Ginger x4, 120 off - correct discount, but NO note and NO
 *   #1112692  Strong Green Tea x3, 200 off - likewise.
 *             Neither order carried a single note attribute, not even the
 *             CartGoal one FoxKit writes on every normal order. That only
 *             happens when no cart was involved: Shopify's Buy-it-now button
 *             builds a checkout straight from the product form.
 *
 *   #1112776  No discount on the order, yet the note described a free unit of
 *             a SKU the order does not contain - the previous state of that
 *             customer's cart. The sync had not run since the cart changed.
 *
 * Every trigger in v10.4 was a timer: 1500ms after page load, 900ms after a
 * cart write, or whenever discount-sync.js finished a pass. A customer who
 * clicks Checkout straight away beats all three, and the cart attributes
 * Shopify freezes into the order are then whatever was there before.
 *
 * Four changes, none of which touch the discount or free-quantity logic:
 *
 *   1. FLUSH BEFORE CHECKOUT. The checkout click is held, syncNote() runs, the
 *      click is replayed. Note and attributes only, through /cart/update.js -
 *      no line key is rewritten and no quantity is sent, so this is not the v9
 *      checkout hook that split cart lines. Bounded by checkoutSyncTimeoutMs;
 *      a slow cart sends the customer on rather than blocking the sale.
 *
 *   2. BUY IT NOW. With an empty cart the click is rerouted through
 *      /cart/add.js, then synced, then on to /checkout - the same checkout the
 *      native button would have produced, but with the attributes present.
 *      With a non-empty cart the native button is left alone, because
 *      rerouting would drag the customer's other items into the purchase.
 *      Wallet buttons are in iframes and are untouched. Any failure falls back
 *      to the native button.
 *
 *   3. FASTER TIMERS. 1500ms -> 500ms on load, 900ms -> 400ms after a cart
 *      write. Narrower gap for anything the flush does not cover.
 *
 *   4. STALENESS TRIPWIRE, attribute _offer_sync. TURNED OFF IN v10.5.2 -
 *      T-Soft rejects any note attribute name it does not already handle, so
 *      order #1112875 failed to import because of it. Do not switch it back on
 *      until T-Soft confirms they handle the key. Records the cart the
 *      data was built from - {"v","items","lines","offers","codes"} - so
 *      #1112776 is detectable instead of silent: if the order's total line
 *      quantity does not equal "items", the offer data belongs to a different
 *      cart and must not be mapped. _offer_data keeps exactly its 8 fields.
 * =============================================================================
 *
 * =============================================================================
 * ADDED IN v10 - Shopify discount codes
 *
 * A line can carry an automatic Discounty campaign AND a coupon the customer
 * typed. Shopify lists both in line_level_discount_allocations, and
 * item.total_discount is the two added together.
 *
 * Earlier versions read total_discount and took allocations[0]'s application
 * key, so a line with a 75.00 offer and a 20.00 coupon was reported as 95.00
 * of offer - money the free-quantity maths could not account for - and the
 * coupon never appeared anywhere. A coupon title such as SAVE20 could also be
 * picked up as the offer name.
 *
 * Allocations are now read one by one and split on
 * discount_application.type. Coupons go to their own attributes and carry no
 * free_quantity, because a coupon is money off rather than goods given away.
 * _offer_data keeps exactly the shape T-Soft already maps.
 * =============================================================================
 *
 * =============================================================================
 * CHANGED IN v9 - line item properties are no longer written
 *
 * v8 wrote _offer_name / _free_quantity / _paid_quantity / _discount_amount
 * onto each cart line as the customer left for checkout. Confirmed live on
 * 28 Sep 2026, that write puts the same product on two checkout lines:
 *
 *   add 3 -> Checkout -> browser back -> add 1
 *   =>  Strong Green Tea x1 (no properties)  +  Strong Green Tea x3
 *
 * Shopify merges two lines of one variant only when their properties match,
 * so a line we have written to can never merge with a later plain add. The
 * totals stayed correct; the order did not. This is what the client reported
 * on order #1110047.
 *
 * linePropertyMode now defaults to 'off', the checkout hook is not attached,
 * and cleanupLineProperties() strips the keys off carts that still carry
 * them. Everything T-Soft maps from lives in cart.note and _offer_data, both
 * unchanged - _offer_data already carries offer_name, free_quantity,
 * paid_quantity and discount_amount per SKU.
 * =============================================================================
 *
 * =============================================================================
 * FIXED IN v6 - read this before changing getDiscountAmount()
 *
 * v5 fell back to the cart-level "_discount" attribute when a line carried no
 * discount of its own, guarded only by a count of how many lines were
 * discounted. On order #1107074 that guard passed (one line was discounted)
 * and the fallback then stamped that same 240.00 onto all seven lines,
 * producing a 1680.00 total against a real discount of 240.00.
 *
 * The fallback has been removed entirely. A line's discount now comes only
 * from that line's own Shopify data. There is no cart-level fallback, and
 * "_discount" is never read - it is written by discount-sync.js and is known
 * to hold only the last processed line's amount rather than the order total.
 * =============================================================================
 */

/**
 * =============================================================================
 * FIXED IN v7 - production quantity bug
 *
 * Symptom: with several products in the cart, changing a quantity would
 * sometimes reset that line to 1, and line properties went out of step with
 * the cart (a line showing quantity 4 carried paid_quantity 5).
 *
 * Cause 1 - /cart/change.js was called WITHOUT a quantity. Shopify's Cart API
 * treats a missing quantity as 1, so every property write silently reset the
 * line. Quantity is now always sent, and it is always the line's current
 * quantity read from a cart fetched moments before.
 *
 * Cause 2 - properties were written on every cart change, racing the theme's
 * own +/- handler over the same line, whose key our write had just changed.
 * Properties are now written once, when the customer goes to checkout.
 *
 * Guards added: per-line validation (quantity must equal free + paid),
 * post-write quantity verification with abort, and a write lock.
 * =============================================================================
 */

class DiscountNoteSync {
  constructor(options = {}) {
    // Bumped on every released change so the console says which build is live.

    this.config = {
      enabled: options.enabled !== false,
      debug: options.debug || false,

      /**
       * MASTER KILL SWITCH.
       *
       * true  = this module reads the cart and computes everything, but never
       *         sends a single write. Use it to prove whether a cart problem
       *         comes from here or from another script.
       *
       * With this on, if quantities still misbehave, the cause is elsewhere -
       * discount-sync.js also calls /cart/change.js per line.
       */
      readOnly: options.readOnly === true,

      // --- Note ---
      notePrefix: options.notePrefix || 'Discount Summary:',

      /**
       * Headings used by earlier builds. composeNote strips from the earliest
       * of these that starts a line, so a summary written months ago is still
       * recognised as ours and replaced instead of being preserved for ever as
       * if the customer had typed it. See composeNote.
       */
      legacyNotePrefixes: options.legacyNotePrefixes || [
        'Discount Summary',
        '=== DISCOUNT SUMMARY',
        'DISCOUNT SUMMARY',
        'Offer Summary',
        'Volume Discount',
      ],
      includeTotal: options.includeTotal !== false,
      maxNoteLength: options.maxNoteLength || 4500,

      /**
       * Largest _offer_data value we will write. Over this the "name" field is
       * dropped; over it even then, the previous value is left in place rather
       * than blanked. See buildAttributes.
       */
      maxAttributeLength: options.maxAttributeLength || 4000,

      /** How long to wait for discount-sync.js before giving up on it. */
      hookTimeoutMs: options.hookTimeoutMs || 15000,

      /** Cap on property-write retries, so a failing write cannot spin. */
      maxPropertyRetries: options.maxPropertyRetries || 3,

      /**
       * Step budget for the exact free-unit search in assignFreeUnits. It is
       * an exhaustive enumeration and grows exponentially with the number of
       * distinct prices in one campaign, so it is capped rather than left to
       * freeze the page. 20,000 steps is microseconds and covers every real
       * cart on this store many times over.
       */
      freeUnitSearchSteps: options.freeUnitSearchSteps || 20000,

      /**
       * Watch for cart writes made by the theme, Shopify or another app, and
       * re-sync afterwards. This is what keeps the discount-code attributes
       * current when a coupon is applied or removed outside discount-sync.js.
       * Observation only - no request is ever altered. See
       * attachCartWriteWatcher.
       */
      watchCartWrites: options.watchCartWrites !== false,

      /**
       * v10.5: 900ms -> 400ms.
       *
       * The debounce exists so a customer holding down "+" does not trigger a
       * sync per click. 900ms was generous enough that a customer who added a
       * product and went straight to checkout navigated away before the sync
       * fired, and the order arrived with no _offer_data at all (orders
       * #1112701, #1112692) or with the previous cart's note still on it
       * (#1112776). 400ms still collapses a burst of clicks, and the checkout
       * flush below is the real safety net.
       */
      watchDebounceMs: options.watchDebounceMs || 400,

      /**
       * One sync on page load, so a cart is never left unsynced. 0 to skip.
       * v10.5: 1500ms -> 500ms, for the same reason as watchDebounceMs. The
       * delay is only there to let the theme finish its own cart work first.
       */
      initialSyncDelayMs:
        options.initialSyncDelayMs === undefined ? 500 : options.initialSyncDelayMs,

      /**
       * FLUSH BEFORE CHECKOUT  (v10.5)
       *
       * Every trigger above is a timer, and a timer loses to a customer who
       * clicks Checkout immediately. This closes the gap: the click is held,
       * syncNote() runs, then the click is replayed.
       *
       * It is safe to hold a checkout click because the flush writes ONLY the
       * note and the cart attributes, through /cart/update.js, which touches
       * neither line keys nor quantities. This is NOT the v9 checkout hook that
       * wrote line item properties through /cart/change.js and split cart
       * lines; that one is still behind linePropertyMode: 'checkout' and still
       * off.
       *
       * checkoutSyncTimeoutMs bounds it. If Shopify is slow the customer goes
       * to checkout anyway with whatever data was already on the cart - a
       * missing attribute is an ERP mapping gap, a blocked checkout is a lost
       * sale.
       */
      syncOnCheckout: options.syncOnCheckout !== false,
      checkoutSyncTimeoutMs: options.checkoutSyncTimeoutMs || 3500,

      /**
       * "BUY IT NOW" / dynamic checkout button  (v10.5)
       *
       * Shopify's own Buy-it-now button submits the PRODUCT form and goes
       * straight to checkout. Nothing we do runs in between, so the order
       * carries no cart attributes at all - not ours, and not FoxKit's either,
       * which is how orders #1112701 and #1112692 were identified as having
       * come through this button.
       *
       * When this is on and the cart is EMPTY, the click is rerouted: the item
       * is added with /cart/add.js, syncNote() runs, then we go to /checkout.
       * The customer ends up at the same checkout with the same single line
       * item, so nothing about the purchase changes - only the attributes are
       * now present.
       *
       * When the cart is NOT empty the native button is used untouched.
       * Rerouting there would pull the customer's other cart items into a
       * checkout they expected to contain one product, which is a worse bug
       * than the one being fixed.
       *
       * Wallet buttons (Shop Pay, PayPal, Google Pay) render inside iframes,
       * so their clicks never reach this listener and they are unaffected.
       *
       * Any failure falls through to the native button, so the worst case is
       * exactly the behaviour before this existed.
       */
      interceptBuyItNow: options.interceptBuyItNow !== false,
      buyItNowTimeoutMs: options.buyItNowTimeoutMs || 6000,

      /**
       * Amounts are plain numbers. The rupee symbol was being stored corrupted
       * in the note ("?240.00"), which is a parsing risk for the ERP.
       *   'plain' -> 240.00   'code' -> INR 240.00
       */
      amountFormat: options.amountFormat || 'plain',
      currencyCode: options.currencyCode || 'INR',
      asciiSafe: options.asciiSafe !== false,

      // --- Cart attributes ---
      writeAttributes: options.writeAttributes !== false,
      jsonPayloadKey: options.jsonPayloadKey || '_offer_data',
      totalKey: options.totalKey || '_offer_total',

      // Shopify discount codes (coupons), kept apart from automatic offers.
      codeKey: options.codeKey || '_discount_code',
      codeDataKey: options.codeDataKey || '_discount_code_data',
      codeTotalKey: options.codeTotalKey || '_discount_code_total',

      /**
       * Staleness tripwire. Records the cart the offer data was built from, so
       * an order can be checked rather than trusted. See buildAttributes.
       *
       * OFF BY DEFAULT SINCE v10.5.2, and it must stay off until T-Soft says
       * otherwise.
       *
       * T-Soft validates note attributes against a fixed list of names and
       * rejects the whole order if it meets one it does not know:
       *
       *   "Failed to save order 112875: New Notes Attribute Name: _offer_sync
       *    introduced, not handled in t-Soft for Order no: 112875"
       *
       * So adding a key here does not just add data - it stops orders being
       * imported at all. #1112875 failed for exactly this reason. Any new
       * attribute name has to be agreed with T-Soft and handled on their side
       * BEFORE it appears on an order.
       *
       * Set syncStampKey: '_offer_sync' to re-enable once they confirm. With
       * it off, the key is covered by the "_offer_" prefix below, so an
       * _offer_sync left on a cart by v10.5 or v10.5.1 is cleared on the next
       * sync rather than lingering.
       */
      syncStampKey: options.syncStampKey || '',

      /**
       * Keys written by earlier versions of this module. Blanked on every
       * write so they disappear from orders. "_discount" and
       * "_volume_discount" belong to discount-sync.js; include them only if
       * that script has been changed to stop writing them, otherwise it will
       * simply write them back and the two scripts will fight.
       */
      legacyKeyPrefixes: options.legacyKeyPrefixes || ['_offer_'],

      /**
       * Our discount-code keys are listed so they are cleared the moment the
       * customer removes the coupon. "_discount_code" is NOT covered by the
       * "_offer_" prefix above, and "_discount" (discount-sync.js) must not be
       * touched, so they are named one by one.
       */
      alsoClearKeys: options.alsoClearKeys || [
        '_discount_code',
        '_discount_code_data',
        '_discount_code_total',
      ],

      /**
       * LINE ITEM PROPERTIES - when to write them
       *
       *   'off' (default)      - never written. See v9 below.
       *   'checkout'           - written ONCE, when the customer heads to
       *                          checkout. Causes the duplicate line described
       *                          in v9. Do not re-enable without reading it.
       *   'cart'               - written on every cart change. This is what
       *                          caused the production quantity bug; kept only
       *                          for debugging.
       *
       * =====================================================================
       * v9 - WHY THIS IS 'off'
       *
       * Shopify merges two cart lines of the same variant ONLY when their
       * line item properties match exactly. Writing properties to a line
       * therefore makes it un-mergeable with any later plain add of the same
       * product.
       *
       * Reproduced live on the storefront, 28 Sep 2026:
       *
       *   add 3  ->  click Checkout (properties written)  ->  browser back
       *          ->  add 1 more
       *
       *   Result at checkout:
       *     Strong Green Tea 200 g  x1  props {}                   150.00
       *     Strong Green Tea 200 g  x3  props {_offer_name: ...}   450.00
       *
       * The same product on two lines. This is what the client reported on
       * order #1110047. The money was correct; the presentation was not.
       *
       * A second fault appeared in the same test: the surviving line still
       * carried _discount_amount "200.00" while its real discount was now
       * 150.00, because Shopify had redistributed the discount across both
       * lines after the properties were written. Properties are a snapshot
       * and go stale whenever the cart changes afterwards.
       *
       * There is no ordering that avoids this. Any property write makes a
       * line un-mergeable, and the cart can always change after the write.
       *
       * Nothing is lost by switching this off: cart.note, _offer_data and
       * _offer_total are unaffected, and those are what T-Soft maps from.
       * The line properties were only ever a convenience copy of data that
       * _offer_data already carries per SKU.
       * =====================================================================
       */
      linePropertyMode: options.linePropertyMode || 'off',

      /**
       * Never let a property write hold up checkout. If the write has not
       * finished within this many ms, the customer proceeds regardless.
       *
       * Raised from 3000. Each line needs its own /cart/change.js call and
       * they must run one after another. On order #1107486, taken from a
       * phone, four lines at roughly 800ms each ran past a 3s budget and the
       * fourth was cut off mid-request - the free line, the one carrying the
       * whole discount. See prewriteIdleMs: the aim now is for there to be
       * nothing left to write by the time this runs.
       */
      checkoutWriteTimeoutMs: options.checkoutWriteTimeoutMs || 8000,

      /**
       * PRE-WRITE - DISABLED BY DEFAULT. Read this before turning it on.
       *
       * Writing properties while the customer is still shopping SPLITS CART
       * LINES. Shopify merges two lines of the same variant only when their
       * properties match exactly. Once we have written properties to a line,
       * the customer adding that same product again arrives with empty
       * properties, so Shopify creates a SECOND line for it.
       *
       * That is what happened on order #1110047: SKU 8906033262465 appears
       * twice, once at quantity 1 and once at quantity 2, with different
       * properties on each - one product, two lines, and the second line's
       * properties left over from an earlier quantity.
       *
       * Properties are therefore written only at checkout, which is the last
       * thing the customer does. Nothing can be added afterwards, so no line
       * can be split.
       *
       * Set to a number of milliseconds only if line splitting has been solved
       * some other way.
       */
      prewriteIdleMs: options.prewriteIdleMs === undefined ? 0 : options.prewriteIdleMs,

      // Selectors that mean "the customer is going to checkout".
      checkoutSelectors:
        options.checkoutSelectors ||
        '[name="checkout"], [href*="/checkout"], .cart__checkout, ' +
          '.cart-drawer__checkout, [data-checkout-button], button[value="Checkout"]',

      // --- Line item properties ---
      /**
       * Kill switch. It used to be defined here and read nowhere, so anyone
       * reaching for the obvious-looking off switch got no effect at all.
       * It now forces linePropertyMode to 'off' below.
       */
      writeLineProperties: options.writeLineProperties !== false,
      linePropertyKeys: options.linePropertyKeys || {
        offerName: '_offer_name',
        freeQuantity: '_free_quantity',
        paidQuantity: '_paid_quantity',
        discountAmount: '_discount_amount',
      },

      /**
       * Property keys written by earlier versions of this module. Removed from
       * a line whenever it is rewritten, otherwise they sit alongside the
       * current keys and reach the order - a line would show both
       * "_offer_discount: 30.00" and "_discount_amount: 0.00" and the ERP
       * would have two conflicting amounts to choose from.
       */
      obsoleteLinePropertyKeys: options.obsoleteLinePropertyKeys || [
        '_offer',
        '_offer_discount',
        '_offer_campaign_tag',
        '_offer_free_qty',
        '_offer_group',
        '_unit_price',
      ],

      // --- Offer name ---
      useProductTags: options.useProductTags !== false,
      offerTagPrefix: options.offerTagPrefix || 'Offer_',
      offerNameKeys: options.offerNameKeys || [
        '_offer_name',
        '_offer',
        'offer_name',
        'Offer',
      ],
      skuPropertyKeys: options.skuPropertyKeys || ['_sku', 'sku', 'SKU'],

      /**
       * v9 - strip line properties left behind by earlier builds. Runs once
       * per page load and only on lines that actually carry them. See
       * cleanupLineProperties(). Set to false to leave old carts alone.
       */
      cleanupLineProperties: options.cleanupLineProperties !== false,

      /**
       * How long the cart must sit still before the cleanup runs, so it can
       * never land in the middle of a customer clicking +/-.
       */
      cleanupDelayMs: options.cleanupDelayMs || 5000,

      debugCartRequests: options.debugCartRequests || false,
    };

    this.state = {
      lastKnownNote: null,
      isUpdatingNote: false,
      isWritingLineProperties: false,
      lastDiscountData: [],
      lastPayload: [],
      offerTagCache: {},
      cartDebugAttached: false,
      warnedOnce: new Set(),
      checkoutHookAttached: false,
      replayingCheckoutClick: false,
      lastPropertyWriteAt: 0,
      prewriteTimer: null,
      failedLines: [],
      cleanupDone: false,
      hookWaitedMs: 0,
      hookTimer: null,
      propertyRetries: 0,
      cartWatcherAttached: false,
      watchTimer: null,
      postingOurUpdate: false,
      lastCart: null,
    };

    // The kill switch wins over whatever mode was asked for.
    if (this.config.writeLineProperties === false) {
      this.config.linePropertyMode = 'off';
    }

    this.initialize();
  }

  initialize() {
    /**
     * Printed unconditionally, not through log(), so the deployed build is
     * identifiable at a glance. Several debugging rounds were spent on a
     * stale file still being served.
     */
    console.log(
      `%c[DiscountNoteSync] ${DiscountNoteSync.VERSION} loaded ` +
        `(linePropertyMode: ${this.config.linePropertyMode}, ` +
        `prewriteIdleMs: ${this.config.prewriteIdleMs})`,
      'color:#006144;font-weight:bold'
    );

    if (this.config.prewriteIdleMs) {
      console.warn(
        '[DiscountNoteSync] prewriteIdleMs is set. Writing line properties while ' +
          'the customer is still shopping splits cart lines - the same product ' +
          'can end up on two lines with different properties. Set it to 0 unless ' +
          'you know why it is on.'
      );
    }

    this.log('Initializing DiscountNoteSync v10...');
    this.hookIntoDiscountSync();
    if (this.config.watchCartWrites) this.attachCartWriteWatcher();

    /**
     * v10.5: attached in every mode, not only 'checkout'.
     *
     * In mode 'off' it flushes the note and attributes; in mode 'checkout' it
     * additionally writes line properties, as before. See attachCheckoutHook.
     */
    if (this.config.syncOnCheckout || this.config.linePropertyMode === 'checkout') {
      this.attachCheckoutHook();
    }

    if (this.config.debugCartRequests) this.attachCartDebugLogging();

    /**
     * One pass on load, so the cart is correct even if discount-sync.js never
     * appears. Until v10 the only trigger was discount-sync.js calling
     * manualSync or syncDiscounts; on a page without it nothing was ever
     * written at all.
     */
    if (this.config.initialSyncDelayMs) {
      setTimeout(() => this.syncNote(), this.config.initialSyncDelayMs);
    }

    /**
     * One-off repair for carts that still carry properties from a build where
     * linePropertyMode was 'checkout'. Delayed so it cannot collide with the
     * first sync or with a customer already clicking +/-.
     */
    if (this.config.cleanupLineProperties && !this.config.readOnly) {
      setTimeout(() => this.cleanupLineProperties(), this.config.cleanupDelayMs);
    }

    this.log(`Ready (linePropertyMode: ${this.config.linePropertyMode})`);
  }

  /**
   * Re-sync after anything else writes to the cart.
   *
   * Applying or removing a discount code does not go through
   * discount-sync.js: Shopify's /discount/CODE link, a theme coupon field, or
   * an app can each change the cart without our hook ever firing. The code
   * attributes then stayed as they were - a removed coupon could sit on the
   * cart until something unrelated happened to trigger a pass.
   *
   * This WATCHES, it never changes a request. fetch and XMLHttpRequest are
   * wrapped, the original is called with the original arguments, and its
   * result is returned untouched; all we do is note that a cart write
   * happened and schedule our own sync a moment later.
   *
   * It cannot loop: our own writes are skipped while postingOurUpdate is set,
   * and a sync that finds nothing to change posts nothing.
   */
  attachCartWriteWatcher() {
    if (this.state.cartWatcherAttached) return;
    if (typeof window === 'undefined') return;
    this.state.cartWatcherAttached = true;

    const isCartWrite = (url) =>
      /\/cart\/(add|change|update|clear)/.test(String(url || '')) ||
      /\/discount\//.test(String(url || ''));

    const noticed = (url) => {
      if (this.state.postingOurUpdate) return; // our own write
      this.log(`Cart write seen (${String(url).split('?')[0]}) - scheduling sync`);
      clearTimeout(this.state.watchTimer);
      this.state.watchTimer = setTimeout(
        () => this.syncNote(),
        this.config.watchDebounceMs
      );
    };

    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch.bind(window);
      window.fetch = (...args) => {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const promise = originalFetch(...args);

        if (isCartWrite(url)) {
          promise.then(() => noticed(url)).catch(() => {});
        }
        return promise;
      };
    }

    if (typeof window.XMLHttpRequest === 'function') {
      const proto = window.XMLHttpRequest.prototype;
      const originalOpen = proto.open;
      const originalSend = proto.send;

      proto.open = function patchedOpen(method, url, ...rest) {
        this.__dnsUrl = url;
        return originalOpen.call(this, method, url, ...rest);
      };

      proto.send = function patchedSend(...args) {
        if (isCartWrite(this.__dnsUrl)) {
          this.addEventListener('load', () => noticed(this.__dnsUrl), { once: true });
        }
        return originalSend.apply(this, args);
      };
    }

    this.log('Watching for cart writes');
  }

  /**
   * Catch the customer heading to checkout and make sure the cart carries
   * current data before they get there.
   *
   * Capture phase, so we run before the theme's own handler. Everything is
   * bounded by a timeout - checkout is never blocked by us, even if Shopify is
   * slow or a request fails.
   *
   * There are two kinds of trigger, and they are NOT interchangeable:
   *
   *   1. A checkout button on the cart page or in the cart drawer. The items
   *      are already in the cart, so a sync now is correct. This is the common
   *      case and the one the timers were losing.
   *
   *   2. A Buy-it-now / dynamic checkout button inside the PRODUCT form. The
   *      item is not in the cart yet. Syncing here would write the previous
   *      cart's data - which is precisely the stale note seen on order
   *      #1112776 - so this case is routed to handleBuyItNow() instead, which
   *      adds the item first and only then syncs.
   *
   * The product form is identified by its action (/cart/add), not by the
   * button, because the same button markup is used for both on some themes.
   */
  attachCheckoutHook() {
    if (this.state.checkoutHookAttached) return;
    if (typeof document === 'undefined' || !document.addEventListener) return;
    this.state.checkoutHookAttached = true;

    const handler = async (event) => {
      // Our own replayed click - let it through untouched.
      if (this.state.replayingCheckoutClick) return;

      const target = event.target;
      if (!target || !target.closest) return;

      let trigger = target.closest(this.config.checkoutSelectors);

      /**
       * Shopify's native Buy-it-now carries none of the usual checkout
       * attributes, so it is matched on its own class. Wallet buttons are
       * excluded: --branded is Shop Pay's own styling, and the rest render
       * inside iframes where this listener never sees the click anyway.
       */
      if (!trigger && this.config.interceptBuyItNow && target.closest) {
        const payButton = target.closest('.shopify-payment-button__button');
        if (
          payButton &&
          !payButton.classList.contains('shopify-payment-button__button--branded')
        ) {
          trigger = payButton;
        }
      }

      if (!trigger) return;

      const form = trigger.closest ? trigger.closest('form') : null;
      const action = form ? String(form.getAttribute('action') || '') : '';
      const isProductForm = action.indexOf('/cart/add') !== -1;

      if (isProductForm) {
        if (!this.config.interceptBuyItNow) return; // native behaviour

        event.preventDefault();
        event.stopImmediatePropagation();
        await this.handleBuyItNow(trigger, form);
        return;
      }

      // Hold the click just long enough to flush the cart data.
      event.preventDefault();
      event.stopImmediatePropagation();

      this.log('Checkout detected - flushing cart data before leaving');

      const budget =
        this.config.linePropertyMode === 'checkout'
          ? this.config.checkoutWriteTimeoutMs
          : this.config.checkoutSyncTimeoutMs;

      try {
        await Promise.race([
          this.flushForCheckout(),
          new Promise((resolve) => setTimeout(resolve, budget)),
        ]);
      } catch (error) {
        this.log('Checkout flush failed, continuing anyway:', error);
      }

      this.resumeCheckout(trigger);
    };

    document.addEventListener('click', handler, true);
    this.log('Checkout hook attached');
  }

  /**
   * What the checkout flush actually does, by mode.
   *
   * Mode 'off' (the production setting): note and attributes only, through
   * /cart/update.js. No line key is rewritten and no quantity is sent, so this
   * cannot split a line or reset a quantity.
   *
   * Mode 'checkout': the old behaviour as well, for debugging only.
   *
   * A sync already in flight is waited out rather than skipped - syncNote()
   * returns immediately when isUpdatingNote is set, and returning here would
   * mean leaving for checkout while the write that matters is still open.
   */
  async flushForCheckout() {
    if (this.state.isUpdatingNote) {
      const until = Date.now() + 2000;
      while (this.state.isUpdatingNote && Date.now() < until) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    }

    await this.syncNote();

    if (this.config.linePropertyMode === 'checkout') {
      await this.writeLinePropertiesNow();
    }
  }

  /**
   * Reroute a Buy-it-now click through the cart, but only when that is
   * provably harmless.
   *
   * Shopify's Buy-it-now builds a checkout straight from the product form. No
   * cart is involved, so no cart attribute can be attached, and the ERP gets
   * an order with no offer data - which is what happened on #1112701 (Ginger
   * x4) and #1112692 (Strong Green Tea x3). Both had zero note_attributes,
   * including the ones FoxKit writes on every normal order, which is what
   * identified the button.
   *
   * Rerouting is only safe with an EMPTY cart. Then "add this product, go to
   * checkout" produces the same checkout the native button would have: one
   * line item, same variant, same quantity, same automatic discount. With a
   * non-empty cart it would silently pull the customer's other items into the
   * purchase, so the native button is used instead and the missing attributes
   * are accepted.
   *
   * Every failure path ends in resumeCheckout(), so a sold-out variant, a
   * network error or a slow cart all behave exactly as they did before this
   * method existed.
   */
  async handleBuyItNow(trigger, form) {
    const native = (why) => {
      this.log(`Buy it now: using the native button (${why})`);
      this.resumeCheckout(trigger);
    };

    if (!form || typeof FormData === 'undefined') {
      native('no usable product form');
      return;
    }

    let done = false;
    const guard = setTimeout(() => {
      if (done) return;
      done = true;
      native('timed out');
    }, this.config.buyItNowTimeoutMs);

    try {
      const cart = await this.fetchCart();

      if (!cart) {
        clearTimeout(guard);
        if (!done) {
          done = true;
          native('cart could not be read');
        }
        return;
      }

      if ((cart.item_count || 0) > 0) {
        clearTimeout(guard);
        if (!done) {
          done = true;
          this.warnOnce(
            'buy-it-now-busy-cart',
            'Buy it now was used while the cart already had items. The native ' +
              'button was used so the other items are not pulled into this ' +
              'checkout - that order will arrive without _offer_data. Hide the ' +
              'dynamic checkout button in the theme settings to avoid this.'
          );
          native('cart is not empty');
        }
        return;
      }

      /**
       * FormData(form) carries the variant id, the quantity and any
       * properties[] the theme or another app put in the form, and it does NOT
       * carry the submit button's own name/value - so no stray "checkout"
       * field is posted. This is a plain add: no properties of ours, so
       * RULE 2 (lines only merge when properties match) is not engaged.
       */
      this.state.postingOurUpdate = true;
      let response;
      try {
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
      } finally {
        setTimeout(() => {
          this.state.postingOurUpdate = false;
        }, 300);
      }

      if (!response || !response.ok) {
        clearTimeout(guard);
        if (!done) {
          done = true;
          // Sold out, a required line property missing, anything: let the real
          // button run so the theme shows the customer its own error.
          native(`add failed with status ${response ? response.status : 'none'}`);
        }
        return;
      }

      await this.syncNote();

      clearTimeout(guard);
      if (done) return; // the timeout already sent them on
      done = true;

      this.log('Buy it now: item added and synced - going to checkout');
      if (typeof window !== 'undefined') window.location.href = '/checkout';
    } catch (error) {
      clearTimeout(guard);
      if (!done) {
        done = true;
        this.log('Buy it now reroute failed:', error);
        native('unexpected error');
      }
    }
  }

  /**
   * Send the customer on to checkout after the property write.
   *
   * This MUST replay a real click on the button rather than calling
   * form.submit().
   *
   * Shopify's cart form posts to /cart, and it is the submit button's own
   * name/value pair ("checkout") that tells Shopify to go to checkout rather
   * than re-render the cart. A programmatic form.submit() does not include the
   * button that "submitted" the form, so that pair is lost and Shopify simply
   * returns the cart page - which is exactly the redirect-back-to-cart bug
   * this replaced.
   *
   * A replayed .click() carries the name/value, and also re-runs any handler
   * the theme has attached, so AJAX checkouts keep working too.
   */
  resumeCheckout(trigger) {
    // Plain link - just follow it.
    if (trigger.tagName === 'A' && trigger.href) {
      window.location.href = trigger.href;
      return;
    }

    this.state.replayingCheckoutClick = true;

    try {
      trigger.click();
    } finally {
      // Cleared on a timer, not immediately: the theme may handle the click
      // asynchronously and we must not intercept its own follow-up.
      setTimeout(() => {
        this.state.replayingCheckoutClick = false;
      }, 1500);
    }

    /**
     * Last resort. If the replayed click did not start a navigation - a theme
     * that blocks duplicate submits, for instance - send the customer to
     * checkout directly. A customer must never be stranded on the cart page
     * because of this module.
     *
     * "Has a navigation started?" is answered by pagehide, which fires as the
     * page is being torn down. The previous version tested
     * visibilityState === 'hidden' and an elapsed-time guard that could never
     * be true (checked at 2000ms against an 1800ms threshold), so neither did
     * anything and the forced navigation could land on top of an AJAX checkout
     * that was already under way.
     */
    this.state.navigatingAway = false;
    const markNavigating = () => {
      this.state.navigatingAway = true;
    };
    window.addEventListener('pagehide', markNavigating, { once: true });
    window.addEventListener('beforeunload', markNavigating, { once: true });

    setTimeout(() => {
      window.removeEventListener('pagehide', markNavigating);
      window.removeEventListener('beforeunload', markNavigating);

      if (this.state.navigatingAway) return; // the browser is already leaving
      if (window.location.pathname.indexOf('/checkout') !== -1) return;

      this.warnOnce(
        'checkout-fallback',
        'Checkout did not start after the replayed click - navigating to /checkout directly.'
      );
      window.location.href = '/checkout';
    }, 2000);
  }

  /**
   * Wrap discount-sync.js so our sync runs after each of its passes.
   *
   * Two things this must survive, because it runs on every page:
   *
   *   - discount-sync.js not being on the page at all. The poll used to have
   *     no end, so a page without it kept a 500ms timer alive for as long as
   *     the tab was open. It now gives up after hookTimeoutMs.
   *   - discount-sync.js being present but a different version, without
   *     manualSync or syncDiscounts. Calling .bind on undefined threw out of
   *     initialize() and out of the constructor, so window.discountNoteSync
   *     was never assigned and the cleanup timer - registered after this call
   *     - was never scheduled. Each method is now checked before wrapping.
   */
  hookIntoDiscountSync() {
    if (!window.discountSync) {
      const waited = this.state.hookWaitedMs || 0;

      if (waited >= this.config.hookTimeoutMs) {
        this.warnOnce(
          'no-discount-sync',
          `DiscountSync did not appear within ${this.config.hookTimeoutMs}ms. ` +
            'Syncing on cart events only.'
        );
        return;
      }

      if (waited === 0) this.log('DiscountSync not found, waiting...');
      this.state.hookWaitedMs = waited + 500;

      clearTimeout(this.state.hookTimer);
      this.state.hookTimer = setTimeout(() => this.hookIntoDiscountSync(), 500);
      return;
    }

    const wrap = (name) => {
      const original = window.discountSync[name];
      if (typeof original !== 'function') {
        this.warnOnce(
          `no-method:${name}`,
          `DiscountSync has no ${name}() - not wrapping it. This is a version ` +
            'mismatch between discount-sync.js and discount-note-sync.js.'
        );
        return false;
      }

      const bound = original.bind(window.discountSync);
      window.discountSync[name] = async (...args) => {
        const result = await bound(...args);
        await this.syncNote();
        return result;
      };
      return true;
    };

    const hooked = ['manualSync', 'syncDiscounts'].filter((n) => wrap(n));

    if (hooked.length === 0) {
      console.warn(
        '[DiscountNoteSync] Could not hook into DiscountSync at all. The note will ' +
          'only update on cart events.'
      );
      return;
    }

    this.log(`Hooked into DiscountSync (${hooked.join(', ')})`);
  }

  /* ==================================================================
   * HELPERS
   * ================================================================ */

  warnOnce(key, ...args) {
    if (this.state.warnedOnce.has(key)) return;
    this.state.warnedOnce.add(key);
    console.warn('[DiscountNoteSync]', ...args);
  }

  normaliseBag(bag) {
    if (!bag) return {};
    if (Array.isArray(bag)) {
      return bag.reduce((acc, e) => {
        if (e && e.name !== undefined) acc[e.name] = e.value;
        return acc;
      }, {});
    }
    return typeof bag === 'object' ? bag : {};
  }

  readKey(bag, keys) {
    const obj = this.normaliseBag(bag);
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const v = obj[key];
        if (v !== null && v !== undefined && String(v).trim() !== '') {
          return { key, value: String(v).trim() };
        }
      }
    }
    return null;
  }

  /**
   * Discount apps often auto-generate a title from the discount VALUE
   * ("240.00 off"). That is the amount restated, not a campaign name.
   */
  isGenericDiscountTitle(title) {
    if (!title) return true;
    const t = String(title).trim().toLowerCase();
    return (
      /^[\d.,]+\s*(off|discount)?$/.test(t) ||
      /^[\d.,]+\s*%\s*(off|discount)?$/.test(t) ||
      /^(rs\.?|inr)\s*[\d.,]+\s*(off|discount)?$/.test(t) ||
      /^(discount|offer|sale|automatic discount|amount off)$/.test(t)
    );
  }

  formatAmount(value) {
    const fixed = value.toFixed(2);
    if (this.config.amountFormat === 'code') return `${this.config.currencyCode} ${fixed}`;
    return fixed;
  }

  toAsciiSafe(text) {
    if (!this.config.asciiSafe) return text;
    return text.replace(/[^\x20-\x7E\n]/g, '');
  }

  /* ==================================================================
   * FIELD RESOLUTION
   * ================================================================ */

  /**
   * Discount for a line, in paise.
   *
   * ONLY this line's own Shopify data is consulted. There is deliberately no
   * cart-level fallback - see the note at the top of this file.
   *
   * Returns 0 for a line Shopify did not discount, which keeps that product
   * out of the offer data entirely.
   */
  /* ==================================================================
   * DISCOUNT ALLOCATIONS - automatic offers vs discount codes
   *
   * A cart line can carry BOTH at once: a Discounty "Buy 4 pay for 3"
   * campaign and a coupon the customer typed. Shopify lists them as two
   * entries in line_level_discount_allocations, and item.total_discount is
   * the two added together.
   *
   * Earlier versions read total_discount and took the FIRST allocation's
   * application key. A line with an offer of 75.00 and a code of 20.00 was
   * therefore reported as 95.00 of offer - the free-quantity maths then had
   * 20.00 of coupon money to account for and could not, and the code was
   * never reported at all.
   *
   * Allocations are now read individually and split by
   * discount_application.type, so the two never mix:
   *
   *   offers (automatic / script)  ->  _offer_data, free/paid quantities
   *   discount codes               ->  _discount_code_data, no free units
   *
   * A coupon is money off, not goods given away, so free_quantity has no
   * meaning for it and is never reported for a code.
   * ================================================================ */

  /**
   * Is this discount application a coupon the customer typed?
   *
   *   discount_code, manual     -> code
   *   automatic, script         -> offer  (Discounty campaigns are automatic)
   *   anything else, or missing -> fallbackIsCode, with a warning
   *
   * WHY THE FALLBACK DEPENDS ON WHERE THE DISCOUNT CAME FROM
   *
   * Real cart.js always sets type, so the fallback is for malformed or future
   * data only - but the two ways of being wrong are not equally bad:
   *
   *   an offer mistaken for a code -> _offer_data comes out EMPTY, every
   *     free_quantity is lost, and T-Soft has nothing to map
   *   a code mistaken for an offer -> the money lands in _offer_total under
   *     the wrong name, but the order still reconciles
   *
   * Treating every unknown as a code looked safer and was not: a build that
   * did so turned all six offer regression carts into zero-offer carts,
   * because their allocations carried no type. So a LINE-level allocation
   * with no type stays an offer - that is where Discounty lives, and it is
   * the behaviour this store already runs on. A CART-level entry that cannot
   * be typed is treated as a code, which is what an order-level coupon is.
   */
  /**
   * The codes the customer actually has on the cart, lowercased.
   *
   * cart.discount_codes is the one reliable signal on this store. Its entries
   * are either plain strings or {code, applicable} objects depending on the
   * Shopify version, so both are handled; a code Shopify marks as not
   * applicable is ignored.
   */
  getAppliedCodeNames(cartData) {
    const cart = cartData || this.state.lastCart;
    const list = (cart && cart.discount_codes) || [];
    const names = [];

    list.forEach((entry) => {
      if (!entry) return;
      if (typeof entry === 'string') {
        names.push(entry.toLowerCase());
        return;
      }
      if (entry.applicable === false) return;
      if (entry.code) names.push(String(entry.code).toLowerCase());
    });

    return names;
  }

  isCodeApplication(type, fallbackIsCode) {
    const t = String(type || '').toLowerCase();
    if (t === 'discount_code' || t === 'manual') return true;
    if (t === 'automatic' || t === 'script') return false;

    /**
     * An EMPTY type is normal here - this store's cart.js sends type: "" on
     * every Discounty allocation - so it is not worth a warning. Whether such
     * an allocation is a coupon has already been settled by matching its
     * title against cart.discount_codes before this is called. Only a type
     * that is present but unrecognised is worth saying something about.
     */
    if (t !== '') {
      this.warnOnce(
        `unknowntype:${t}:${fallbackIsCode ? 'c' : 'o'}`,
        `Discount application type "${t}" is not one Shopify documents. ` +
          `Treating it as ${fallbackIsCode ? 'a discount code' : 'an automatic offer'}.`
      );
    }

    return !!fallbackIsCode;
  }

  /**
   * Every discount on a line, tagged offer or code.
   *
   * Shopify splits this across two places and only their sum is on the line:
   *
   *   item.line_level_discount_allocations  LINE-level only, carries the type
   *   cart.cart_level_discount_applications CART-level, no per-line split
   *   item.discounts                        BOTH, but only {amount, title}
   *   item.total_discount                   BOTH added together
   *
   * An "Amount off order" discount code - the ordinary coupon shape - is
   * CART-level, so it never appears in line_level_discount_allocations. The
   * first version of this function read only that array and returned early
   * whenever it held anything, so on a cart with a Discounty campaign plus a
   * coupon the coupon's money vanished from every attribute and from the
   * note, and nothing warned.
   *
   * So: line-level allocations are read directly, then item.discounts is
   * matched by title against the cart-level applications to recover the
   * cart-level share of this line. Titles already accounted for at line level
   * are skipped, because item.discounts is the union of the two and would
   * otherwise be counted twice.
   */
  getLineAllocations(item, cartData) {
    const out = [];
    const seenTitles = [];

    /**
     * THE TITLE TEST COMES FIRST, BECAUSE type IS EMPTY ON THIS STORE.
     *
     * Confirmed from the live cart on 12 Oct 2026: Discounty's allocations
     * arrive as type: "" - not "automatic", not missing, an empty string:
     *
     *   { amount: 4000, discount_application: {
     *       type: "", key: "6a66489d-...", title: "120.00 off",
     *       value_type: "fixed_amount", allocation_method: "across" } }
     *
     * So type cannot separate an offer from a coupon here. cart.discount_codes
     * can: it lists exactly the codes the customer has applied. An application
     * whose title is one of those codes IS that code; everything else on the
     * line is an automatic offer.
     *
     * Without this, a line-level coupon ("amount off products") would be
     * booked as offer money under the coupon's own name - which is what used
     * to happen - while an order-level one was caught only because cart-level
     * applications do carry a type.
     */
    const appliedCodes = this.getAppliedCodeNames(cartData);
    const titleIsCode = (title) =>
      !!title && appliedCodes.indexOf(String(title).toLowerCase()) !== -1;

    if (Array.isArray(item.line_level_discount_allocations)) {
      item.line_level_discount_allocations.forEach((alloc) => {
        const amount = Number(alloc && alloc.amount) || 0;
        if (amount <= 0) return;

        const app = (alloc && alloc.discount_application) || {};
        const title = app.title ? String(app.title).trim() : null;

        if (title) seenTitles.push(title);

        out.push({
          amount_minor: amount,
          title,
          key: app.key ? String(app.key) : null,
          isCode: titleIsCode(title) || this.isCodeApplication(app.type, false),
          level: 'line',
        });
      });
    }

    // Cart-level applications, indexed by title so item.discounts can be typed.
    const cart = cartData || this.state.lastCart;
    const cartApps = {};
    if (cart && Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach((app) => {
        if (!app || !app.title) return;
        cartApps[String(app.title).trim()] = app;
      });
    }

    if (Array.isArray(item.discounts)) {
      item.discounts.forEach((d) => {
        const amount = Number(d && d.amount) || 0;
        if (amount <= 0) return;

        const title = d.title ? String(d.title).trim() : null;

        // Already counted at line level - item.discounts includes those too.
        if (title && seenTitles.indexOf(title) !== -1) return;

        const app = title ? cartApps[title] : null;

        if (app) {
          out.push({
            amount_minor: amount,
            title,
            key: app.key ? String(app.key) : null,
            isCode: titleIsCode(title) || this.isCodeApplication(app.type, true),
            level: 'cart',
          });
          return;
        }

        /**
         * In item.discounts but in neither the line allocations nor the
         * cart-level applications. Reported as a code so the money is not
         * swept into the offer maths, and flagged so it can be looked at.
         */
        this.warnOnce(
          `untyped:${title}`,
          `Discount "${title}" (${(amount / 100).toFixed(2)}) is on the line but in ` +
            'neither line_level_discount_allocations nor ' +
            'cart_level_discount_applications. Reporting it as a discount code.'
        );

        out.push({
          amount_minor: amount,
          title,
          key: null,
          isCode: true,
          level: 'cart',
        });
      });
    }

    /**
     * Last resort: the line says it was discounted but listed nothing. The
     * amount is reported as an offer so it is not lost, with no campaign name.
     */
    if (out.length === 0 && typeof item.total_discount === 'number' && item.total_discount > 0) {
      out.push({
        amount_minor: item.total_discount,
        title: null,
        key: null,
        isCode: false,
        level: 'line',
      });
    }

    return out;
  }

  /**
   * Automatic-offer money on this line.
   *
   * Read only from this line's own allocations. There is NO cart-level
   * "_discount" fallback - that is what stamped one line's 240.00 onto seven
   * lines on order #1107074 and reported 1680.00. See the v6 note at the top.
   */
  getLineDiscount(item, cartData) {
    return this.getLineAllocations(item, cartData)
      .filter((a) => !a.isCode)
      .reduce((sum, a) => sum + a.amount_minor, 0);
  }

  /** Discount-code money on this line. */
  getLineCodeDiscount(item, cartData) {
    return this.getLineAllocations(item, cartData)
      .filter((a) => a.isCode)
      .reduce((sum, a) => sum + a.amount_minor, 0);
  }

  /**
   * Per-SKU breakdown of discount-code money, one row per line per code.
   *
   * A code that takes money off nothing - free shipping, or a code that only
   * applies to a product not in the cart - produces no rows but is still
   * recorded in _discount_code, so the order shows which code was used.
   */
  extractCodeData(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return [];

    const rows = [];

    cartData.items.forEach((item) => {
      this.getLineAllocations(item, cartData).forEach((alloc) => {
        if (!alloc.isCode) return;
        rows.push({
          sku: this.getSku(item) || 'UNKNOWN',
          code: alloc.title || 'UNKNOWN',
          quantity: item.quantity,
          discount_amount: (alloc.amount_minor / 100).toFixed(2),
        });
      });
    });

    return rows;
  }

  /**
   * Codes that are applied to the cart but take nothing off a line, such as
   * a free-shipping code. Without this the keys would be blanked on the next
   * sync and the order would carry no sign that a code was used at all.
   */
  extractCodeNames(cartData) {
    const names = [];

    /**
     * cart.discount_codes first: it is the list of codes the customer has
     * applied, whatever they ended up taking off. A free-shipping code, or
     * one for a product that is not in the cart, allocates nothing to a line
     * and would otherwise leave no trace on the order at all.
     */
    const applied = (cartData && cartData.discount_codes) || [];
    applied.forEach((entry) => {
      if (!entry) return;
      const code = typeof entry === 'string' ? entry : entry.code;
      if (!code) return;
      if (typeof entry === 'object' && entry.applicable === false) return;
      const clean = String(code).trim();
      if (clean && names.indexOf(clean) === -1) names.push(clean);
    });

    const apps = (cartData && cartData.cart_level_discount_applications) || [];
    apps.forEach((app) => {
      if (!app || !app.title) return;
      if (!this.isCodeApplication(app.type, true)) return;
      const title = String(app.title).trim();
      if (title && names.indexOf(title) === -1) names.push(title);
    });

    return names;
  }

  /**
   * SKU from the cart line - Shopify populates this from the variant actually
   * in the cart, so multi-variant products resolve correctly. Never falls back
   * to the product title.
   */
  getSku(item) {
    if (item.sku && String(item.sku).trim() !== '') return String(item.sku).trim();

    const prop = this.readKey(item.properties, this.config.skuPropertyKeys);
    if (prop) return prop.value;

    this.warnOnce(
      `nosku:${item.variant_id}`,
      'MISSING SKU - product title will NOT be substituted. Fix this product in Shopify:',
      { product_id: item.product_id, variant_id: item.variant_id, title: item.product_title }
    );

    return null;
  }

  /**
   * Fetch the "Offer_" tag for a product handle. That tag is what the
   * storefront renders as the offer badge, so it is the offer name the
   * customer actually saw.
   */
  async fetchOfferTag(handle) {
    if (!handle) return null;
    if (Object.prototype.hasOwnProperty.call(this.state.offerTagCache, handle)) {
      return this.state.offerTagCache[handle];
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`status ${response.status}`);

      const product = await response.json();
      const tags = Array.isArray(product.tags) ? product.tags : [];
      const prefix = this.config.offerTagPrefix;

      const matches = tags
        .map((t) => String(t).trim())
        .filter((t) => t.indexOf(prefix) === 0)
        .map((t) => t.substring(prefix.length).trim())
        .filter(Boolean);

      const name = matches.length > 0 ? matches[0] : null;
      this.state.offerTagCache[handle] = name;
      return name;
    } catch (error) {
      this.log(`Could not fetch tags for ${handle}:`, error);
      this.state.offerTagCache[handle] = null;
      return null;
    }
  }

  /** Load tags for discounted lines only - undiscounted products are ignored. */
  async preloadOfferTags(cartData) {
    if (!this.config.useProductTags) return;
    if (!cartData || !Array.isArray(cartData.items)) return;

    const handles = [
      ...new Set(
        cartData.items
          .filter((i) => this.getLineDiscount(i) > 0)
          .map((i) => i.handle)
          .filter(Boolean)
      ),
    ];

    await Promise.all(handles.map((h) => this.fetchOfferTag(h)));
  }

  resolveOfferName(item, cartData) {
    const named = this.readKey(item.properties, this.config.offerNameKeys);
    if (named) return named.value;

    if (this.config.useProductTags && item.handle) {
      const tagName = this.state.offerTagCache[item.handle];
      if (tagName) return tagName;
    }

    /**
     * Shopify's own discount title, if it is a real name and not the amount.
     *
     * Discount-code allocations are excluded. A coupon title such as SAVE10
     * is not amount-shaped, so isGenericDiscountTitle would have let it
     * through and the coupon code would have been reported as the offer name
     * on every line it touched.
     */
    const titles = this.getLineAllocations(item, cartData)
      .filter((a) => !a.isCode && a.title)
      .map((a) => a.title);

    const real = titles.filter((t) => !this.isGenericDiscountTitle(t));
    if (real.length > 0) return real[0];

    if (titles.length > 0) {
      this.warnOnce(
        `generic:${titles[0]}`,
        `Discount title "${titles[0]}" is the amount restated, not a campaign name. ` +
          'Add an Offer_ tag to the product, or set the campaign label in Discounty.'
      );
    }

    return null;
  }

  /**
   * Which applied discount produced this line's discount. Lines sharing this
   * key came from the same campaign - the Mix & Match case.
   */
  /**
   * Which offer campaign this line belongs to. Discount-code allocations are
   * skipped: taking allocations[0] blindly put a line under the coupon's key
   * whenever the coupon happened to be listed first, which split one campaign
   * across two groups and broke its free-quantity maths.
   */
  getApplicationKey(item, cartData) {
    const offers = this.getLineAllocations(item, cartData).filter((a) => !a.isCode);
    if (offers.length === 0) return null;

    /**
     * EVERY offer application on the line, not just the first.
     *
     * getLineDiscount sums all of them, so keying on allocations[0] alone put
     * a line's whole discount under one campaign while a second campaign on
     * the same line was never named anywhere. Lines sharing the same set of
     * campaigns now group together and the group's money stays whole; if the
     * combined amount does not match a single Buy X Pay Y rule, the OFFER
     * MISMATCH check in buildOfferPayload reports it rather than quietly
     * producing a wrong free_quantity.
     */
    if (offers.length > 1) {
      this.warnOnce(
        `multioffer:${offers.map((o) => o.title || o.key).join('+')}`,
        `More than one automatic discount on one line: ` +
          `${offers.map((o) => `"${o.title || o.key}" ${(o.amount_minor / 100).toFixed(2)}`).join(', ')}. ` +
          'free_quantity is worked out from the first rule that parses, so check it.'
      );
    }

    return offers
      .map((o) => o.key || (o.title ? `title:${o.title}` : 'untitled'))
      .sort()
      .join('+');
  }

  /* ==================================================================
   * OFFER RULE
   * ================================================================ */

  /**
   * Read the Buy X Pay Y rule out of the offer name.
   *   "Buy 4 pay for 3" -> { eligible: 4, paid: 3, free: 1 }
   *
   * The rule is not present anywhere in the cart or order payload - Discounty
   * applies a fixed amount at a quantity threshold - so the offer name is the
   * only place it exists. Returns null if unreadable.
   */
  parseOfferRule(offerName) {
    if (!offerName) return null;

    const m = String(offerName).match(
      /buy\s*(\d+)\s*(?:,\s*)?(?:pay|get|for)\s*(?:for\s*)?(\d+)/i
    );
    if (!m) return null;

    const eligible = parseInt(m[1], 10);
    const paid = parseInt(m[2], 10);
    if (!eligible || !paid || paid >= eligible) return null;

    return { eligible, paid, free: eligible - paid };
  }

  /* ==================================================================
   * DISCOUNT DATA
   * ================================================================ */

  /**
   * One row per DISCOUNTED line. Products Shopify did not discount are
   * excluded here and therefore never reach the note, the attributes or the
   * line properties.
   */
  extractDiscountData(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return [];

    const rows = [];

    cartData.items.forEach((item) => {
      const discountMinor = this.getLineDiscount(item, cartData);
      if (discountMinor <= 0) return; // no offer on this product

      rows.push({
        key: item.key,
        sku: this.getSku(item),
        product_title: item.product_title || item.title,
        quantity: item.quantity,
        unit_price_minor: Number(item.original_price) || Number(item.price) || 0,
        discount_minor: discountMinor,
        offer_name: this.resolveOfferName(item, cartData),
        application_key: this.getApplicationKey(item, cartData),
      });
    });

    this.state.lastDiscountData = rows;
    return rows;
  }

  /** Group rows by the campaign that produced them. */
  groupByApplication(rows) {
    const groups = [];
    const index = {};

    rows.forEach((row) => {
      const key = row.application_key || `line:${row.key}`;
      if (!index[key]) {
        index[key] = { offer_name: row.offer_name, lines: [], total_minor: 0 };
        groups.push(index[key]);
      }
      index[key].lines.push(row);
      index[key].total_minor += row.discount_minor;
      if (!index[key].offer_name && row.offer_name) index[key].offer_name = row.offer_name;
    });

    return groups;
  }

  /**
   * THE PAYLOAD - exactly eight fields, offer products only.
   *
   * free_quantity comes from the OFFER RULE, not from dividing the discount by
   * the price. Free units are assigned from the last line of the group
   * backwards, so on a Mix & Match cart the final eligible product is marked
   * free rather than every product showing a fraction.
   *
   * discount_amount is Shopify's ACTUAL discount, redistributed onto the lines
   * marked free. Rule-implied value and Shopify's value agree when the group's
   * products share a price and diverge when they do not; Shopify's figure wins
   * so that SUM(discount_amount) always equals the real discount.
   */
  /**
   * Decide WHICH units in an offer group are the free ones.
   *
   * The free units have to be worth exactly what Shopify took off, because
   * T-Soft rebuilds the invoice as paid_quantity x MRP. Picking the wrong
   * product leaves the ERP's total short.
   *
   * Order #1112219 showed why the price has to drive this. Two "Buy 4 pay
   * for 3" campaigns ran at once: one over the 150.00 teas (Vanilla + Rose,
   * 75.00 allocated to each line, 150.00 in total) and one over the 120.00
   * teas (Spearmint + Ginger + Lemon, 40.00 each, 120.00 in total). Each
   * campaign gives away ONE unit at ITS OWN price. Taking simply the last
   * line in the group happened to be right there because each group held a
   * single price - but a campaign spanning 150.00 and 120.00 products would
   * have marked a 150.00 tea free against a 120.00 discount, and T-Soft's
   * invoice would have come out 30.00 under what the customer paid.
   *
   * So the free units must be worth exactly the discount Shopify applied.
   * Within that constraint they are SPREAD ACROSS THE LINES in proportion to
   * what each line bought, rather than piled onto one.
   *
   * Order #1112321 is why. Three teas at 120.00, four of each, twelve units
   * in all, so "Buy 4 pay for 3" gives three free. Taking the last line put
   * all three free units on the Masala line - "quantity 4, free 3, paid 1" -
   * while Ginger and Elaichi showed four paid and nothing free. The totals
   * were right and the invoice still came to 1,080.00, but it described
   * something the customer never saw: they bought four of each and got one
   * of each free. One free per line is the honest reading.
   *
   * Proportional rather than one-each, because one-each is wrong the other
   * way round: with 1 of product A and 7 of B (8 units, 2 free), one-each
   * would make A entirely free and bill none of it, when both free units
   * clearly belong to B.
   *
   * If no proportional split can be made to equal the discount - which only
   * happens when a campaign spans several prices - the exact-sum search below
   * takes over, preferring cheaper units, as a Buy X Get Y discount normally
   * resolves. Returns {lineKey: freeCount}.
   */
  assignFreeUnits(group, freeTotal) {
    /**
     * PROPORTIONAL SPLIT, largest remainder.
     *
     * Ties go to the LAST line, so a group where every line is equal keeps
     * the behaviour this store ran on before: one free unit on the last
     * eligible product. Order #1112219 is unchanged by this; #1112321 goes
     * from 0/0/3 to 1/1/1.
     */
    const totalQty = group.lines.reduce((a, r) => a + r.quantity, 0);

    if (totalQty > 0) {
      /**
       * The remainder is kept as an INTEGER, not a fraction.
       *
       * Ranking on (freeTotal * qty) / totalQty - floor(...) looks equivalent
       * and is not: a double keeps full precision below 1 and loses low bits
       * once the integer part is non-zero. For quantities 1, 4, 4 with 3 free
       * units over 9, every true remainder is 1/3, but they compute as
       *
       *   qty 1 -> 0.3333333333333333
       *   qty 4 -> 0.33333333333333326   (lower, so it sorts last)
       *
       * The 1-unit line then won the tie and took the spare free unit, so a
       * customer who bought one of something had it billed as paid_quantity 0
       * - the very outcome proportional splitting was chosen to avoid.
       *
       * (freeTotal * qty) % totalQty is the same ordering in exact integers.
       */
      const shares = group.lines.map((line, i) => {
        const numerator = freeTotal * line.quantity;
        const whole = Math.min(Math.floor(numerator / totalQty), line.quantity);
        return { i, line, whole, remainder: numerator % totalQty };
      });

      let left = freeTotal - shares.reduce((a, s) => a + s.whole, 0);

      // Biggest remainder first; equal remainders -> the later line.
      const queue = shares
        .slice()
        .sort((a, b) => (b.remainder - a.remainder) || (b.i - a.i));

      // Several passes, so a capped line hands its unit to another.
      while (left > 0) {
        let placed = false;
        for (const s of queue) {
          if (left === 0) break;
          if (s.whole >= s.line.quantity) continue;
          s.whole += 1;
          left -= 1;
          placed = true;
        }
        if (!placed) break; // every line is full
      }

      const proportional = {};
      let impliedMinor = 0;
      shares.forEach((s) => {
        if (s.whole > 0) proportional[s.line.key] = s.whole;
        impliedMinor += s.whole * s.line.unit_price_minor;
      });

      if (impliedMinor === group.total_minor) return proportional;
    }

    const available = new Map();
    group.lines.forEach((r) => {
      available.set(r.unit_price_minor, (available.get(r.unit_price_minor) || 0) + r.quantity);
    });

    const prices = Array.from(available.keys()).sort((a, b) => a - b);
    const counts = new Array(prices.length).fill(0);
    let solution = null;

    /**
     * Search over distinct PRICES, not units, cheapest price at its highest
     * count first, so the cheapest-free reading wins when several combinations
     * fit.
     *
     * BOUNDED, because this runs on the storefront's main thread. It is a full
     * enumeration of count vectors, and when no combination can reach the
     * target it explores all of them: measured on this code, a group with 12
     * distinct prices took 2.4 seconds and 14 took 63, growing roughly 4.7x
     * per added price. That is a frozen page while the customer is shopping.
     *
     * The budget stops it long before that. Running out means no exact split
     * was found in time; the caller then uses the last-line fallback and
     * reports the mismatch, which is what it does for a genuinely impossible
     * group anyway.
     */
    let budget = this.config.freeUnitSearchSteps;

    const search = (i, left, sum) => {
      if (solution) return;
      if (budget <= 0) return;
      budget -= 1;
      if (sum > group.total_minor) return;
      if (left === 0) {
        if (sum === group.total_minor) solution = counts.slice();
        return;
      }
      if (i >= prices.length) return;

      const most = Math.min(left, available.get(prices[i]));
      for (let take = most; take >= 0; take -= 1) {
        counts[i] = take;
        search(i + 1, left - take, sum + take * prices[i]);
        counts[i] = 0;
        if (solution || budget <= 0) return;
      }
    };

    search(0, freeTotal, 0);

    if (!solution && budget <= 0) {
      this.warnOnce(
        `searchbudget:${group.offer_name}`,
        `Gave up looking for an exact free-unit split on "${group.offer_name}" after ` +
          `${this.config.freeUnitSearchSteps} steps (${prices.length} distinct prices). ` +
          'Falling back to the last eligible line.'
      );
    }

    const perLine = {};

    if (!solution) {
      // Nothing adds up. Fall back to the last eligible line, as before.
      let remaining = freeTotal;
      for (let i = group.lines.length - 1; i >= 0 && remaining > 0; i -= 1) {
        const line = group.lines[i];
        const take = Math.min(line.quantity, remaining);
        if (take > 0) perLine[line.key] = take;
        remaining -= take;
      }
      return perLine;
    }

    // Turn per-price counts into per-line counts, last line of a price first.
    prices.forEach((price, i) => {
      let left = solution[i];
      for (let n = group.lines.length - 1; n >= 0 && left > 0; n -= 1) {
        const line = group.lines[n];
        if (line.unit_price_minor !== price) continue;
        const take = Math.min(line.quantity, left);
        if (take > 0) perLine[line.key] = (perLine[line.key] || 0) + take;
        left -= take;
      }
    });

    return perLine;
  }

  /**
   * How many free units the rule earns - and, crucially, HOW the campaign
   * counts quantity.  (v10.5.1)
   *
   * Discounty campaigns on this store count in two different ways, and the
   * Offer_ tag cannot tell them apart because both read "Buy 5 pay for 4":
   *
   *   POOLED    "Mix of products" / "All items together". The whole group's
   *             quantity is added up and the tier applied once. The tea
   *             campaigns (72403, 105318, 72375) work this way. Order
   *             #1112733 depends on it: four lines of 2 pool to 8 and earn
   *             floor(8/4) = 2 free units, even though no single line reaches
   *             the tier of 4.
   *
   *   PER LINE  "Each item independently". Every variant has to reach the
   *             tier on its own. The incense campaigns (71746 at 20 off per 5,
   *             72398 at 80 off per 5) work this way.
   *
   * Pooling a per-line campaign over-counts. 7 Rose + 3 Sandalwood at 20.00
   * pools to 10 and claims floor(10/5) = 2 free units worth 40.00, while
   * Shopify only took 20.00 off - because Rose reached 5 and Sandalwood did
   * not. An ERP billing paid_quantity x price would then bill 8 units instead
   * of 9 and under-charge the customer by one unit.
   *
   * The campaign's counting mode is not in the cart, the product tags or
   * anything else the storefront can read. What IS available is the amount
   * Shopify actually took off, and that settles it: work out both
   * distributions and keep the one worth exactly that amount. The rule still
   * decides the free count - this only picks which way the rule counts.
   *
   * Lower pooled totals are tried last, for a tier only partly met.
   *
   * If nothing matches, the pooled answer is returned with unreconciled set,
   * which is the pre-v10.5.1 behaviour plus the warning in buildOfferPayload.
   */
  chooseFreeDistribution(group, rule, pooledTotal) {
    const worth = (dist) =>
      group.lines.reduce((a, l) => a + (dist[l.key] || 0) * l.unit_price_minor, 0);
    const count = (dist) => Object.keys(dist).reduce((a, k) => a + dist[k], 0);

    const candidates = [];

    // 1. Pooled - the tea campaigns, and the historical default.
    if (pooledTotal > 0) {
      candidates.push({ mode: 'pooled', dist: this.assignFreeUnits(group, pooledTotal) });
    }

    // 2. Per line - the incense campaigns. Never more than pooled, because
    //    sum(floor(qty_i / n)) <= floor(sum(qty_i) / n).
    const byLine = {};
    group.lines.forEach((l) => {
      const n = Math.floor(l.quantity / rule.eligible) * rule.free;
      if (n > 0) byLine[l.key] = Math.min(n, l.quantity);
    });
    if (count(byLine) > 0) candidates.push({ mode: 'per-line', dist: byLine });

    // 3. Fewer pooled units. Bounded so a large cart cannot spin here.
    const floor = Math.max(1, pooledTotal - 24);
    for (let k = pooledTotal - 1; k >= floor; k -= 1) {
      candidates.push({ mode: `pooled-${k}`, dist: this.assignFreeUnits(group, k) });
    }

    for (const c of candidates) {
      if (worth(c.dist) === group.total_minor) {
        return { perLine: c.dist, freeTotal: count(c.dist), mode: c.mode };
      }
    }

    const fallback = candidates[0] || { mode: 'none', dist: {} };
    return {
      perLine: fallback.dist,
      freeTotal: count(fallback.dist),
      mode: fallback.mode,
      unreconciled: true,
    };
  }

  buildOfferPayload(rows) {
    const resolved = {};

    this.groupByApplication(rows).forEach((group) => {
      const rule = this.parseOfferRule(group.offer_name);
      const totalQty = group.lines.reduce((a, r) => a + r.quantity, 0);

      if (!rule) {
        if (group.offer_name) {
          this.warnOnce(
            `norule:${group.offer_name}`,
            `Could not read a Buy X Pay Y rule from "${group.offer_name}". ` +
              'Reporting free_quantity as 0 and keeping Shopify\'s per-line amounts.'
          );
        }
        group.lines.forEach((r) => {
          resolved[r.key] = { free: 0, discount_minor: r.discount_minor };
        });
        return;
      }

      const freeTotal = Math.floor(totalQty / rule.eligible) * rule.free;

      if (freeTotal <= 0) {
        /**
         * v10.5: this path used to be silent, and it is the one that produces
         * "free quantity is not calculated".
         *
         * The rule in the Offer_ tag says no free unit is earned at this
         * quantity, yet Shopify took money off the group - so the campaign
         * that actually fired is not the campaign the tag describes. Example:
         * Strong Green Tea under campaign 72375 ("Buy 3 Pay for 2", 200 off at
         * 3 items) while the product carries Offer_Buy 4 pay for 3. At
         * quantity 3 the rule earns floor(3/4) = 0 free units, so the order
         * went out with 200.00 of discount and free_quantity 0 and nothing
         * said why.
         *
         * free_quantity stays 0 rather than being back-calculated from the
         * money: a genuine amount-off-cart campaign gives no free units at
         * all, and inventing one there would be worse. The fix belongs on the
         * tag, which is why this names both sides.
         */
        if (group.total_minor > 0) {
          this.warnOnce(
            `norule-money:${group.offer_name}:${totalQty}`,
            `OFFER RULE MISMATCH on "${group.offer_name}": the tag earns ` +
              `floor(${totalQty}/${rule.eligible}) x ${rule.free} = 0 free units at ` +
              `quantity ${totalQty}, but Shopify took ` +
              `${(group.total_minor / 100).toFixed(2)} off this group. free_quantity is ` +
              'reported as 0 and the full amount stays in discount_amount, so ' +
              '_offer_total still reconciles - but an ERP billing ' +
              'paid_quantity x price will bill the whole quantity. Make the Offer_ ' +
              'tag match the Discounty campaign that is actually running on these ' +
              'products.'
          );
        }
        group.lines.forEach((r) => {
          resolved[r.key] = { free: 0, discount_minor: r.discount_minor };
        });
        return;
      }

      const chosen = this.chooseFreeDistribution(group, rule, freeTotal);
      const perLine = chosen.perLine;
      const effectiveFree = chosen.freeTotal;

      group.lines.forEach((r) => {
        resolved[r.key] = { free: perLine[r.key] || 0, discount_minor: 0 };
      });

      const implied = group.lines.reduce(
        (a, r) => a + (perLine[r.key] || 0) * r.unit_price_minor,
        0
      );

      if (implied === group.total_minor) {
        /**
         * The free units we picked are worth exactly what Shopify took off.
         * Each line's discount is then simply its own free units at its own
         * price, so free_quantity x price == discount_amount holds on every
         * line as well as across the group. This is the case an ERP that
         * bills paid_quantity x MRP depends on.
         */
        group.lines.forEach((r) => {
          resolved[r.key].discount_minor = (perLine[r.key] || 0) * r.unit_price_minor;
        });
      } else {
        /**
         * No combination of free units adds up to Shopify's figure, so the
         * two cannot both be honoured. The group total is kept exact - that
         * is what has to reconcile with the payment - and spread over the
         * free lines, which leaves free_quantity x price != discount_amount
         * on those lines.
         *
         * An ERP billing paid_quantity x MRP will be out by the difference,
         * so this is an error rather than a note.
         */
        const freeKeys = Object.keys(perLine);
        let distributed = 0;

        freeKeys.forEach((key, i) => {
          const amount =
            i === freeKeys.length - 1
              ? group.total_minor - distributed // last absorbs the remainder
              : Math.round((group.total_minor * perLine[key]) / effectiveFree);
          if (i !== freeKeys.length - 1) distributed += amount;
          resolved[key].discount_minor = amount;
        });

        this.warnOnce(
          `mismatch:${group.offer_name}:${group.total_minor}`,
          `OFFER MISMATCH on "${group.offer_name}" (counting mode tried: ` +
            `${chosen.mode}): ${effectiveFree} free item(s) can only ` +
            `be made to total ${(implied / 100).toFixed(2)}, but Shopify took off ` +
            `${(group.total_minor / 100).toFixed(2)}. _offer_total stays correct, but ` +
            'an ERP billing paid_quantity x price will be out by ' +
            `${Math.abs(implied - group.total_minor) / 100}. Check that the Discounty ` +
            'campaign and the Offer_ tag describe the same rule.'
        );
      }
    });

    const payload = rows.map((r) => {
      const res = resolved[r.key] || { free: 0, discount_minor: r.discount_minor };
      return {
        sku: r.sku || 'UNKNOWN',
        name: r.product_title,
        price: (r.unit_price_minor / 100).toFixed(2),
        quantity: r.quantity,
        offer_name: r.offer_name,
        free_quantity: res.free,
        paid_quantity: r.quantity - res.free,
        discount_amount: (res.discount_minor / 100).toFixed(2),
      };
    });

    this.state.lastPayload = payload;
    return payload;
  }

  /* ==================================================================
   * NOTE
   * ================================================================ */

  /**
   * Discount Summary:
   *
   * SKU: 8906033261222
   * Offer: Buy 4 pay for 3
   * Qty: 8
   * Free Qty: 2
   * Discount: 240.00
   *
   * Total: 240.00
   */
  /**
   * Put our summary back into the note WITHOUT destroying what the customer
   * typed.
   *
   * cart.note is a shared field. Minmog renders an order-instructions textarea
   * on the cart page and in the drawer, and posts it straight to cart.note.
   * Earlier versions replaced the whole note with ours, so:
   *
   *   customer types "Please deliver after 6pm"
   *   -> customer removes the last discounted item
   *   -> buildDiscountNote returns '' and we posted note: ''
   *   -> the delivery instruction is gone, silently.
   *
   * Everything from notePrefix onwards is treated as ours and rebuilt;
   * anything before it is the customer's and is carried through untouched.
   * When there is no discount left we write back the customer's text alone
   * rather than an empty string.
   */
  composeNote(liveNote, ourNote) {
    const live = String(liveNote || '');

    /**
     * v10.5: the earliest of several markers, not just the current prefix.
     *
     * A note written by an older build used a different heading. indexOf on
     * the current prefix alone returned -1, so that whole block was treated as
     * customer-typed text and carried forward for ever - a stale summary no
     * version could clear, which is what order #1112776 looked like.
     *
     * Matched at the start of a line only, so a sentence a customer happens to
     * write containing the words is left alone.
     */
    let marker = -1;
    const candidates = [this.config.notePrefix].concat(
      this.config.legacyNotePrefixes || []
    );

    candidates.forEach((prefix) => {
      if (!prefix) return;
      const at = this.findLineStart(live, prefix);
      if (at !== -1 && (marker === -1 || at < marker)) marker = at;
    });

    const customer = (marker === -1 ? live : live.slice(0, marker)).trim();

    if (!ourNote) return customer;
    if (!customer) return ourNote;
    return `${customer}\n\n${ourNote}`;
  }

  /** Index of `needle` where it begins a line, case-insensitively. -1 if none. */
  findLineStart(text, needle) {
    const hay = String(text).toLowerCase();
    const pin = String(needle).toLowerCase();
    let at = hay.indexOf(pin);
    while (at !== -1) {
      if (at === 0 || hay[at - 1] === '\n' || hay[at - 1] === '\r') return at;
      at = hay.indexOf(pin, at + 1);
    }
    return -1;
  }

  /**
   * v10.5.3 - lines Shopify split off the same variant are added back together.
   *
   * Order #1114864 (1 Nov 2026): 5 x Black Tea with Natural Masala under
   * "Buy 4 pay for 3". Shopify kept the cart as two lines of one variant -
   * 4 carrying the offer, 1 at full price - and merged them into a single
   * line of 5 on the order. Only the discounted line was in _offer_data, so
   * the order carried Qty 4 / Paid 3 against an order line of 5: T-Soft
   * billed 3 x 120 = 360 for a line worth 480.
   *
   * Entries of the same variant are summed, and the full-price units on
   * lines without an offer are added as paid. quantity then equals the
   * order line, free + paid == quantity still holds, and the free units and
   * discount_amount are exactly what they were. Same eight fields.
   *
   * mergeSameSku: false restores one entry per cart line.
   */
  mergePayloadBySku(payload, rows, cartData) {
    if (this.config.mergeSameSku === false) return payload;
    if (!cartData || !Array.isArray(cartData.items)) return payload;
    if (!Array.isArray(payload) || payload.length === 0) return payload;

    const variantOfKey = {};
    cartData.items.forEach((i) => {
      variantOfKey[i.key] = i.variant_id;
    });

    const out = [];
    const byVariant = {};
    rows.forEach((r, i) => {
      const p = payload[i];
      if (!p) return;
      const v = variantOfKey[r.key] || `k:${r.key}`;
      if (!byVariant[v]) {
        byVariant[v] = { ...p, _minor: Math.round(parseFloat(p.discount_amount) * 100) };
        out.push(byVariant[v]);
        return;
      }
      const m = byVariant[v];
      m.quantity += p.quantity;
      m.free_quantity += p.free_quantity;
      m.paid_quantity += p.paid_quantity;
      m._minor += Math.round(parseFloat(p.discount_amount) * 100);
      if (!m.offer_name) m.offer_name = p.offer_name;
    });

    // Full-price units Shopify split off: paid, nothing off.
    const counted = {};
    rows.forEach((r) => {
      counted[r.key] = true;
    });
    cartData.items.forEach((i) => {
      if (counted[i.key]) return;
      const m = byVariant[i.variant_id];
      if (!m) return;
      m.quantity += i.quantity;
      m.paid_quantity += i.quantity;
    });

    const merged = out.map((m) => {
      const { _minor, ...rest } = m;
      return { ...rest, discount_amount: (_minor / 100).toFixed(2) };
    });

    if (merged.length !== payload.length || merged.some((m, i) => m.quantity !== payload[i].quantity)) {
      this.log('Merged split lines per SKU:', merged.map((m) => `${m.sku} x${m.quantity}`).join(', '));
    }

    this.state.lastPayload = merged;
    return merged;
  }

  buildDiscountNote(payload, codeRows) {
    const codes = Array.isArray(codeRows) ? codeRows : [];
    if ((!payload || payload.length === 0) && codes.length === 0) return '';

    /**
     * Only lines that actually carry money are listed.
     *
     * On a Mix & Match cart every participating product appears in
     * _offer_data - T-Soft needs the full SKU mapping - but the discount sits
     * on one of them. Listing the others here as "Discount: 0.00" adds noise
     * to the summary a human reads in the Shopify admin, so they are omitted.
     * _offer_data is NOT filtered.
     */
    const discounted = (payload || []).filter((p) => parseFloat(p.discount_amount) > 0);
    if (discounted.length === 0 && codes.length === 0) return '';

    const blocks = [];
    let total = 0;

    discounted.forEach((p) => {
      const lines = [`SKU: ${p.sku}`];
      if (p.offer_name) lines.push(`Offer: ${p.offer_name}`);
      lines.push(`Qty: ${p.quantity}`);
      lines.push(`Free Qty: ${p.free_quantity}`);
      lines.push(`Discount: ${this.formatAmount(parseFloat(p.discount_amount))}`);
      blocks.push(lines.join('\n'));
      total += parseFloat(p.discount_amount);
    });

    /**
     * Discount codes are listed per SKU too, so a human reading the admin can
     * see which product the coupon money came off. "Free Qty" is deliberately
     * absent: a coupon is money off, not goods given away.
     */
    codes.forEach((c) => {
      blocks.push(
        [
          `SKU: ${c.sku}`,
          `Discount Code: ${c.code}`,
          `Qty: ${c.quantity}`,
          `Discount: ${this.formatAmount(parseFloat(c.discount_amount))}`,
        ].join('\n')
      );
      total += parseFloat(c.discount_amount);
    });

    let note = `${this.config.notePrefix}\n\n${blocks.join('\n\n')}`;

    if (this.config.includeTotal) {
      note += `\n\nTotal: ${this.formatAmount(total)}`;
    }

    note = this.toAsciiSafe(note);

    if (note.length > this.config.maxNoteLength) {
      console.warn(`[DiscountNoteSync] Note too long (${note.length}); truncating.`);
      note = note.substring(0, this.config.maxNoteLength - 20) + '\n...[truncated]';
    }

    return note;
  }

  /* ==================================================================
   * ATTRIBUTES
   * ================================================================ */

  /**
   * Only two keys are written: _offer_data and _offer_total.
   *
   * Anything left on the cart from an earlier version (_offer_1, _offer_2,
   * _offer_count and so on) is blanked, so old orders' key shapes do not
   * linger on a returning customer's cart.
   */
  buildAttributes(payload, liveAttributes, codeRows, codeNames, cartData) {
    const attributes = {};
    const active = [];
    const live = this.normaliseBag(liveAttributes);

    if (payload.length > 0) {
      const limit = this.config.maxAttributeLength;
      let json = JSON.stringify(payload);

      /**
       * OVERSIZED PAYLOAD - never blank what is already there.
       *
       * The first version simply skipped the key when the JSON ran long. The
       * blanking loop below then saw _offer_data on the live cart, found it
       * missing from `active`, and wrote _offer_data: "" - so a cart that grew
       * past the limit lost its line detail while _offer_total stayed, handing
       * the ERP a discount total with nothing to attribute it to.
       *
       * Now the payload is slimmed first: `name` is by far the longest field
       * and T-Soft can read the same text from line_items[].title. If even the
       * slim form will not fit, the existing value is left untouched.
       */
      if (json.length > limit) {
        const slim = payload.map((p) => {
          const copy = { ...p };
          delete copy.name;
          return copy;
        });
        const slimJson = JSON.stringify(slim);

        this.warnOnce(
          'json-too-long',
          `Offer JSON is ${json.length} chars, over the ${limit} limit. Dropping the ` +
            `"name" field (${slimJson.length} chars) - read it from line_items[].title.`
        );

        json = slimJson;
      }

      if (json.length <= limit) {
        attributes[this.config.jsonPayloadKey] = json;
        active.push(this.config.jsonPayloadKey);

        const total = payload.reduce((a, p) => a + parseFloat(p.discount_amount), 0);
        attributes[this.config.totalKey] = total.toFixed(2);
        active.push(this.config.totalKey);
      } else {
        /**
         * THE TOTAL IS HELD BACK TOO.
         *
         * Writing a fresh _offer_total beside a breakdown we could not update
         * leaves the ERP with, say, 400.00 of discount against lines summing
         * to 5.00 - and nothing on the order says the two are from different
         * moments. Both keys stay on their last consistent pair instead.
         */
        console.error(
          `[DiscountNoteSync] Offer JSON is ${json.length} chars even without "name" ` +
            `and cannot be written. ${this.config.jsonPayloadKey} and ` +
            `${this.config.totalKey} are both left at their previous values so they ` +
            'stay consistent. Raise maxAttributeLength if Shopify accepts more.'
        );
        active.push(this.config.jsonPayloadKey);
        active.push(this.config.totalKey);
      }
    }

    /**
     * DISCOUNT CODES - written to their own keys, never mixed into
     * _offer_data.
     *
     * Keeping them separate means T-Soft's existing _offer_data mapping does
     * not change at all: the same 8 fields, one entry per SKU, free/paid
     * quantities that still reconcile on their own. A coupon has no free
     * units, so folding it in would have put money into _offer_data that no
     * free_quantity accounts for.
     *
     *   _discount_code        the code(s), comma separated
     *   _discount_code_data   [{sku, code, quantity, discount_amount}]
     *   _discount_code_total  their total
     *
     * Reconciliation for the whole order:
     *   _offer_total + _discount_code_total == order.total_discounts
     */
    const codes = Array.isArray(codeRows) ? codeRows : [];
    const extraNames = Array.isArray(codeNames) ? codeNames : [];

    // Codes that took money off a line, plus any that took none (free
    // shipping, or a code for a product that is not in the cart).
    const names = [];
    codes.forEach((c) => {
      if (c.code && names.indexOf(c.code) === -1) names.push(c.code);
    });
    extraNames.forEach((n) => {
      if (n && names.indexOf(n) === -1) names.push(n);
    });

    if (names.length > 0) {
      attributes[this.config.codeKey] = this.toAsciiSafe(names.join(', '));
      active.push(this.config.codeKey);
    }

    if (codes.length > 0) {
      const codeJson = JSON.stringify(codes);
      const codeTotal = codes.reduce((a, c) => a + parseFloat(c.discount_amount), 0);

      if (codeJson.length <= this.config.maxAttributeLength) {
        attributes[this.config.codeDataKey] = codeJson;
        active.push(this.config.codeDataKey);

        attributes[this.config.codeTotalKey] = codeTotal.toFixed(2);
        active.push(this.config.codeTotalKey);
      } else {
        // Same rule as the offer payload: breakdown and total move together.
        console.error(
          `[DiscountNoteSync] Discount-code JSON is ${codeJson.length} chars, over the ` +
            `${this.config.maxAttributeLength} limit. ${this.config.codeDataKey} and ` +
            `${this.config.codeTotalKey} are both left at their previous values. ` +
            `The codes themselves are still in ${this.config.codeKey}.`
        );
        active.push(this.config.codeDataKey);
        active.push(this.config.codeTotalKey);
      }
    }

    /**
     * STALENESS TRIPWIRE  (v10.5, new key: _offer_sync)
     *
     * Order #1112776 arrived with no discount at all and a note describing a
     * free unit of a SKU that was not in the order - data left over from an
     * earlier state of that customer's cart. Nothing on the order said so, and
     * an ERP reading the note had no way to tell.
     *
     * This records what the cart looked like at the moment the data was built:
     *
     *   {"v":"10.5","items":8,"lines":4,"offers":4,"codes":0}
     *
     *   items   total units in the cart  (cart.item_count)
     *   lines   number of cart lines
     *   offers  entries in _offer_data
     *   codes   entries in _discount_code_data
     *
     * T-Soft's check is one comparison: sum(order.line_items[].quantity) must
     * equal "items". If it does not, the offer data is from a different cart
     * and must be ignored rather than mapped.
     *
     * Deliberately no timestamp. A value that changed on every pass would make
     * attributesChanged true on every cart read and produce a /cart/update.js
     * write per keystroke. This changes only when the cart does.
     *
     * _offer_data itself is untouched - still exactly the 8 fields.
     */
    const syncKey = this.config.syncStampKey;
    if (syncKey) {
      const cart = cartData || this.state.lastCart || {};
      const stamp = {
        v: String(DiscountNoteSync.VERSION).replace(/^v/, '').split(' ')[0],
        items: Number(cart.item_count || 0),
        lines: Array.isArray(cart.items) ? cart.items.length : 0,
        offers: payload.length,
        codes: codes.length,
      };
      attributes[syncKey] = JSON.stringify(stamp);
      active.push(syncKey);
    }

    // Blank leftovers from earlier versions, and our own keys once they no
    // longer apply - a removed coupon must not leave _discount_code behind.
    Object.keys(live).forEach((key) => {
      if (active.includes(key)) return;

      const isOurs = this.config.legacyKeyPrefixes.some((p) => key.indexOf(p) === 0);
      const isListed = this.config.alsoClearKeys.includes(key);

      if ((isOurs || isListed) && live[key] !== '') {
        attributes[key] = '';
      }
    });

    return attributes;
  }

  /* ==================================================================
   * LINE ITEM PROPERTIES
   * ================================================================ */

  /**
   * Write the offer onto each discounted line via /cart/change.js, so the
   * order carries it inside line_items[] next to the SKU.
   *
   * CAUTIONS:
   *  1. Changing properties changes the line KEY. Other scripts holding a key
   *     will find it stale.
   *  2. Each write is a cart mutation and may trigger a discount recalculation.
   *     The writes are idempotent - a line already correct is skipped - so the
   *     sequence settles rather than looping.
   *  3. Writes must be sequential; concurrent /cart/change.js calls race.
   *  4. Keys start with "_" so they stay hidden from the customer.
   */
  /**
   * Write the offer onto each discounted line.
   *
   * SAFETY RULES - every one of these exists because of a production incident:
   *
   * 1. QUANTITY IS ALWAYS SENT, and it is always the line's CURRENT quantity
   *    read from a cart fetched moments earlier. Shopify's Cart API states
   *    that if quantity is omitted from /cart/change.js it is assumed to be 1,
   *    so omitting it does not "leave it alone" - it silently resets the line
   *    to 1. That was the cause of the quantity resets. We never compute or
   *    alter a quantity; we echo back exactly what the customer chose.
   *
   * 2. THE CART IS RE-READ IMMEDIATELY BEFORE WRITING, so line keys and
   *    quantities are current rather than from a stale snapshot.
   *
   * 3. EVERY LINE IS VALIDATED before it is written. If
   *    quantity !== free + paid, that line is skipped and reported. Invalid
   *    data never reaches Shopify.
   *
   * 4. THE CART IS RE-READ AFTER EACH WRITE and the quantity is compared. If
   *    it moved, the whole operation stops and reports instead of trying to
   *    correct itself.
   *
   * 5. WRITES ARE SEQUENTIAL and guarded by a lock, so two passes can never
   *    interleave on the same cart.
   */
  /**
   * Queue a property write for when the cart has been quiet for a while.
   *
   * Every cart change pushes the timer back, so nothing is written while the
   * customer is still clicking +/-. Once they stop, the properties are written
   * in the background - and the checkout-time pass then has nothing left to
   * do, which is what stops slow connections from cutting the last line off.
   */
  schedulePrewrite() {
    if (this.config.readOnly) return;
    if (this.config.linePropertyMode === 'off') return;
    if (!this.config.prewriteIdleMs) return;

    clearTimeout(this.state.prewriteTimer);

    this.state.prewriteTimer = setTimeout(() => {
      if (this.state.isUpdatingNote || this.state.isWritingLineProperties) {
        // Something is in flight - try again shortly.
        this.schedulePrewrite();
        return;
      }
      this.log('Cart idle - pre-writing line properties');
      this.writeLinePropertiesNow();
    }, this.config.prewriteIdleMs);
  }

  async writeLinePropertiesNow() {
    if (this.config.readOnly) {
      this.log('readOnly is on - no line properties will be written');
      return null;
    }
    if (this.config.linePropertyMode === 'off') return null;
    if (this.state.isWritingLineProperties) {
      this.log('Property write already running');
      return null;
    }

    const keys = this.config.linePropertyKeys;

    try {
      this.state.isWritingLineProperties = true;

      // RULE 2 - always work from a freshly fetched cart.
      let cart = await this.fetchCart();
      if (!cart || !Array.isArray(cart.items)) return null;

      await this.preloadOfferTags(cart);
      let rows = this.extractDiscountData(cart);
      let payload = this.buildOfferPayload(rows);

      if (payload.length === 0) {
        this.log('No discounted lines - nothing to write');
        return cart;
      }

      // Pair each payload entry with its live cart line.
      const planned = [];

      rows.forEach((row, i) => {
        const entry = payload[i];
        if (!entry) return;

        const item = cart.items.find((it) => it.key === row.key);
        if (!item) {
          this.warnOnce(
            `gone:${row.key}`,
            `Line ${row.sku} is no longer in the cart - skipping its properties.`
          );
          return;
        }

        // RULE 3 - never write data that contradicts itself.
        if (entry.free_quantity + entry.paid_quantity !== item.quantity) {
          console.error(
            `[DiscountNoteSync] REFUSING to write invalid properties for ${entry.sku}: ` +
              `cart quantity is ${item.quantity} but free(${entry.free_quantity}) + ` +
              `paid(${entry.paid_quantity}) = ${entry.free_quantity + entry.paid_quantity}. ` +
              'Skipping this line.'
          );
          return;
        }

        const existing = this.normaliseBag(item.properties);

        // Keep anything that is not ours and not obsolete.
        const carried = {};
        Object.keys(existing).forEach((k) => {
          if (this.config.obsoleteLinePropertyKeys.includes(k)) return;
          carried[k] = existing[k];
        });

        const desired = {};
        if (entry.offer_name) desired[keys.offerName] = entry.offer_name;
        desired[keys.freeQuantity] = String(entry.free_quantity);
        desired[keys.paidQuantity] = String(entry.paid_quantity);
        desired[keys.discountAmount] = entry.discount_amount;

        const hasObsolete = Object.keys(existing).some((k) =>
          this.config.obsoleteLinePropertyKeys.includes(k)
        );
        const valuesDiffer = Object.keys(desired).some(
          (k) => String(existing[k] || '') !== String(desired[k])
        );

        if (hasObsolete || valuesDiffer) {
          planned.push({
            key: row.key,
            variant_id: item.variant_id,
            sku: entry.sku,
            quantity: item.quantity, // RULE 1 - echo the customer's quantity
            properties: { ...carried, ...desired },
          });
        }
      });

      if (planned.length === 0) {
        this.log('Line properties already correct');
        return cart;
      }

      // RULE 5 - one at a time.
      this.state.failedLines = [];

      for (const change of planned) {
        const before = change.quantity;

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify({
            id: change.key,
            quantity: change.quantity, // RULE 1 - MUST be sent, see above
            properties: change.properties,
          }),
        });

        if (!response.ok) {
          this.state.failedLines.push(change.sku);
          this.warnOnce(
            `lineprop:${change.sku}`,
            `Could not write properties for ${change.sku} (status ${response.status}). ` +
              'The line key was probably changed by another script. Will retry.'
          );
          continue;
        }

        cart = await response.json();

        // RULE 4 - confirm we did not disturb the quantity.
        const after = cart.items
          .filter((i) => i.variant_id === change.variant_id)
          .reduce((a, i) => a + i.quantity, 0);

        if (after !== before) {
          console.error(
            `[DiscountNoteSync] ABORTING: quantity for ${change.sku} changed from ` +
              `${before} to ${after} during a property write. No further writes will ` +
              'be made in this pass.'
          );
          this.config.linePropertyMode = 'off';
          break;
        }
      }

      this.state.lastPropertyWriteAt = Date.now();

      /**
       * Report anything that did not make it. A line silently missing its
       * properties is exactly the failure that reached order #1107486, where
       * the free line - the one carrying the whole discount - arrived empty.
       */
      const written = planned.length - this.state.failedLines.length;
      this.log(`Line properties written for ${written}/${planned.length} line(s)`);

      /**
       * Retries are CAPPED. failedLines is cleared at the top of every pass,
       * so without a counter a write that keeps failing rearms this timer for
       * the life of the page - and a 429 from Shopify's cart endpoint is
       * exactly the failure that repeats. That turns into a request every
       * 1.2s, which keeps the rate limit engaged and slows the customer's own
       * +/- clicks. The counter is reset by a clean pass.
       */
      if (this.state.failedLines.length > 0) {
        if (this.state.propertyRetries >= this.config.maxPropertyRetries) {
          console.warn(
            `[DiscountNoteSync] ${this.state.failedLines.length} line(s) still without ` +
              `properties after ${this.config.maxPropertyRetries} retries: ` +
              `${this.state.failedLines.join(', ')}. Giving up - the cart is unharmed.`
          );
        } else {
          this.state.propertyRetries += 1;
          console.warn(
            `[DiscountNoteSync] ${this.state.failedLines.length} line(s) did not get ` +
              `their properties: ${this.state.failedLines.join(', ')}. Retrying ` +
              `(${this.state.propertyRetries}/${this.config.maxPropertyRetries}).`
          );
          setTimeout(() => this.writeLinePropertiesNow(), 1200);
        }
      } else {
        this.state.propertyRetries = 0;
      }

      return cart;
    } catch (error) {
      this.log('Line property write failed:', error);
      return null;
    } finally {
      this.state.isWritingLineProperties = false;
    }
  }

  /** Backwards-compatible alias. */
  async syncLineProperties() {
    return this.writeLinePropertiesNow();
  }

  /* ==================================================================
   * v9 CLEANUP - remove line properties written by earlier builds
   *
   * Switching linePropertyMode to 'off' stops NEW properties being written,
   * but carts already carrying them keep them for as long as Shopify keeps
   * the cart (about two weeks). Those lines stay un-mergeable, so the very
   * duplicate this release removes would go on appearing for returning
   * customers.
   *
   * This strips our own keys back off, once per page load, and only when a
   * line actually has them. Anything written by other apps is carried over
   * untouched.
   *
   * Clearing the properties is what MERGES a split pair back into one line,
   * so on a cart that already shows the duplicate this is the repair.
   *
   * The same five safety rules as writeLinePropertiesNow apply: fresh cart,
   * quantity always sent, one write at a time, quantity verified afterwards,
   * abort on any surprise. Set cleanupLineProperties: false to skip it.
   * ================================================================ */
  async cleanupLineProperties() {
    if (this.config.readOnly) return null;
    if (this.config.cleanupLineProperties === false) return null;
    if (this.state.cleanupDone) return null;
    if (this.state.isWritingLineProperties) return null;

    const ours = Object.values(this.config.linePropertyKeys).concat(
      this.config.obsoleteLinePropertyKeys
    );

    /** A line still carrying our keys, taken from a cart fetched just now. */
    const findStale = (cart) => {
      if (!cart || !Array.isArray(cart.items)) return null;
      return (
        cart.items.find((item) => {
          const bag = this.normaliseBag(item.properties);
          return Object.keys(bag).some((k) => ours.includes(k));
        }) || null
      );
    };

    try {
      this.state.isWritingLineProperties = true;
      this.state.cleanupDone = true; // one attempt per page, whatever happens

      /**
       * _offer_name is one of the keys being removed, and resolveOfferName
       * reads it before anything else. On a product with no Offer_ tag whose
       * Discounty title is just the amount ("200.00 off"), that property can
       * be the only surviving source of the offer name - and without a name
       * parseOfferRule cannot run, so free_quantity silently becomes 0 while
       * discount_amount stays right and nothing looks wrong.
       *
       * The property is still removed, because leaving it is what splits cart
       * lines, but the merchant is told exactly which product needs a tag.
       */
      const startCart = await this.fetchCart();
      if (startCart) {
        await this.preloadOfferTags(startCart);
        startCart.items.forEach((item) => {
          const bag = this.normaliseBag(item.properties);
          if (!Object.keys(bag).some((k) => ours.includes(k))) return;

          const stripped = { ...item, properties: {} };
          if (!this.resolveOfferName(stripped)) {
            console.warn(
              `[DiscountNoteSync] ${item.sku || item.title} has no Offer_ tag. Once its ` +
                'old _offer_name property is removed the offer name cannot be resolved, ' +
                'so free_quantity will read 0. Add an Offer_ tag to this product.'
            );
          }
        });
      }

      let cleared = 0;

      /**
       * ONE LINE PER PASS, EACH FROM A CART FETCHED THAT INSTANT.
       *
       * The first draft of this function planned every write up front from a
       * single snapshot and then sent that snapshot's quantity. With
       * cleanupDelayMs at 5000 and roughly 800ms per write, a customer
       * clicking "+" during the loop would have had their click silently
       * undone: we would have posted the old quantity back. The same snapshot
       * would also have overwritten any property another app (Rebuy, FoxKit)
       * added in the meantime, because "keep everything that is not ours" was
       * computed against a stale bag.
       *
       * Re-fetching before every write removes both: the quantity and the
       * carried properties always come from the cart as it is right now, and
       * the before/after comparison is against a genuinely independent read.
       *
       * The loop is bounded by maxPasses so a line that refuses to clear can
       * never spin.
       */
      const maxPasses = (this.config.cleanupMaxPasses || 12);

      for (let pass = 0; pass < maxPasses; pass += 1) {
        // RULE 2 - always work from a cart fetched this instant.
        const cart = await this.fetchCart();
        const live = findStale(cart);
        if (!live) break;

        const bag = this.normaliseBag(live.properties);
        const removing = Object.keys(bag).filter((k) => ours.includes(k));

        // Keep every property that is not ours, as it stands RIGHT NOW.
        const carried = {};
        Object.keys(bag).forEach((k) => {
          if (ours.includes(k)) return;
          carried[k] = bag[k];
        });

        const sku = live.sku || live.title;

        /**
         * Summed across every line of this variant on purpose: clearing the
         * properties is expected to MERGE two lines into one, so the line
         * count legitimately drops while the total must not move. Read from
         * the same fresh cart, so it is a real check rather than a restatement
         * of what we are about to send.
         */
        const before = cart.items
          .filter((i) => i.variant_id === live.variant_id)
          .reduce((a, i) => a + i.quantity, 0);

        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify({
            id: live.key,
            quantity: live.quantity, // RULE 1 - the customer's LIVE quantity
            properties: carried,
          }),
        });

        if (!response.ok) {
          this.warnOnce(
            `cleanup:${sku}`,
            `Could not clear old properties on ${sku} (status ${response.status}). ` +
              'Leaving the line alone.'
          );
          break;
        }

        const updated = await response.json();

        // RULE 4 - confirm the customer's quantity survived.
        const after = updated.items
          .filter((i) => i.variant_id === live.variant_id)
          .reduce((a, i) => a + i.quantity, 0);

        if (after !== before) {
          console.error(
            `[DiscountNoteSync] ABORTING cleanup: quantity for ${sku} changed ` +
              `from ${before} to ${after}. No further cleanup writes.`
          );
          break;
        }

        cleared += 1;
        this.log(`Cleared ${removing.join(', ')} from ${sku}`);
      }

      if (cleared > 0) this.log(`Old line properties removed from ${cleared} line(s)`);
      return cleared;
    } catch (error) {
      this.log('Line property cleanup failed:', error);
      return null;
    } finally {
      this.state.isWritingLineProperties = false;
    }
  }

  /* ==================================================================
   * SYNC
   * ================================================================ */

  async syncNote() {
    if (!this.config.enabled) return;
    if (this.state.isUpdatingNote) {
      this.log('Update already in progress');
      return;
    }

    try {
      this.state.isUpdatingNote = true;

      let cartData = await this.fetchCart();
      if (!cartData || !cartData.items) {
        this.log('Failed to fetch cart');
        return;
      }

      await this.preloadOfferTags(cartData);

      const rows = this.extractDiscountData(cartData);
      /**
       * v10.5.3 - one entry per SKU. See mergePayloadBySku. Merged here, after
       * the free-unit maths, so the note and _offer_data both see it and the
       * maths itself is unchanged.
       */
      const payload = this.mergePayloadBySku(this.buildOfferPayload(rows), rows, cartData);
      let codeRows = this.extractCodeData(cartData);
      let codeNames = this.extractCodeNames(cartData);

      /**
       * NOTE: line item properties are deliberately NOT written here.
       *
       * This method runs on every cart change, including while the customer
       * is clicking the +/- buttons. Writing properties calls
       * /cart/change.js, which rewrites the line key and requires a quantity -
       * doing that mid-interaction is what caused quantities to reset.
       *
       * Only the note and the cart attributes are written here, both via
       * /cart/update.js, which touches neither line keys nor quantities.
       *
       * Properties are written once at checkout - see attachCheckoutHook().
       * Set linePropertyMode: 'cart' to restore the old behaviour for
       * debugging, understanding that it reintroduces the race.
       */
      if (this.config.linePropertyMode === 'cart') {
        await this.writeLinePropertiesNow();
        cartData = (await this.fetchCart()) || cartData;
        codeRows = this.extractCodeData(cartData);
        codeNames = this.extractCodeNames(cartData);
      }

      const liveNote = (cartData.note || '').trim();
      const note = this.composeNote(liveNote, this.buildDiscountNote(payload, codeRows));
      const noteChanged = note.trim() !== liveNote;

      /**
       * Note and attributes are compared separately. An unchanged note does
       * not imply current attributes - after a code change the note text can
       * be identical while the attribute shape has moved on.
       */
      let attributes = null;
      let attributesChanged = false;

      if (this.config.writeAttributes) {
        const live = this.normaliseBag(cartData.attributes);
        attributes = this.buildAttributes(payload, live, codeRows, codeNames, cartData);
        attributesChanged = Object.keys(attributes).some(
          (k) => String(attributes[k] || '') !== String(live[k] || '')
        );
      }

      if (!noteChanged && !attributesChanged) {
        this.log('Note and attributes already correct');
        this.schedulePrewrite();
        return;
      }

      const body = {};
      if (noteChanged) body.note = note || '';
      if (attributesChanged) body.attributes = attributes;

      this.log(`Updating: note=${noteChanged}, attributes=${attributesChanged}`);
      await this.postCartUpdate(body);

      this.state.lastKnownNote = note;
      this.schedulePrewrite();
    } catch (error) {
      this.log('Error in syncNote:', error);
    } finally {
      this.state.isUpdatingNote = false;
    }
  }

  async postCartUpdate(payload) {
    if (this.config.readOnly) {
      this.log('readOnly is on - would have written:', payload);
      return null;
    }

    // Tell the cart-write watcher this one is ours, so it does not re-trigger.
    this.state.postingOurUpdate = true;

    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);

      const result = await response.json();
      this.log('Cart update accepted');
      return result;
    } finally {
      // Cleared on a timer: the watcher fires when the response lands.
      setTimeout(() => {
        this.state.postingOurUpdate = false;
      }, 300);
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      /**
       * Kept so getLineAllocations can see cart_level_discount_applications
       * while looking at a single line. A line carries no record of which
       * cart-level discount produced part of its total_discount - only the
       * cart does.
       */
      this.state.lastCart = cart;
      return cart;
    } catch (error) {
      this.log('Fetch error:', error);
      return null;
    }
  }

  /* ==================================================================
   * DEBUG / VALIDATION
   * ================================================================ */

  attachCartDebugLogging() {
    if (this.state.cartDebugAttached) return;
    if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

    this.state.cartDebugAttached = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);
      try {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart\/(add|change)(\.js)?/.test(url) && !this.state.isUpdatingNote) {
          setTimeout(() => this.inspect(), 400);
        }
      } catch (e) {
        /* never break the host request */
      }
      return response;
    };

    this.log('Cart debug logging attached');
  }

  /** Console-only inspection. Writes nothing. */
  async inspect() {
    const cart = await this.fetchCart();
    if (!cart || !Array.isArray(cart.items)) return;

    await this.preloadOfferTags(cart);
    const rows = this.extractDiscountData(cart);
    const payload = this.buildOfferPayload(rows);

    console.log('=== ALL CART LINES ===');
    console.table(
      cart.items.map((i) => ({
        sku: i.sku,
        qty: i.quantity,
        line_discount: (this.getLineDiscount(i) / 100).toFixed(2),
        in_offer_data: this.getLineDiscount(i) > 0 ? 'YES' : 'no',
      }))
    );

    if (payload.length === 0) {
      console.log('No discounted lines.');
      return payload;
    }

    console.log('=== _offer_data ===');
    console.table(payload);
    console.log(JSON.stringify(payload, null, 2));
    console.log('=== NOTE ===\n' + this.buildDiscountNote(this.mergePayloadBySku(payload, rows, cart), this.extractCodeData(cart)));

    return payload;
  }

  /** Pass/fail checks against the live cart. */
  async verify() {
    const cart = await this.fetchCart();
    if (!cart) return console.error('[verify] Could not fetch cart');

    await this.preloadOfferTags(cart);
    const rows = this.extractDiscountData(cart);
    const payload = this.buildOfferPayload(rows);

    const sum = payload.reduce((a, p) => a + parseFloat(p.discount_amount), 0);
    const discountedLines = cart.items.filter((i) => this.getLineDiscount(i) > 0).length;
    const attrs = this.normaliseBag(cart.attributes);

    /**
     * Coupons are counted here too. _offer_data is offer money only, while
     * cart.total_discount is offers plus codes, so comparing the two on their
     * own reported FAIL on every correct cart that had a coupon on it - and a
     * check that cries wolf is worse than no check.
     */
    const codeRows = this.extractCodeData(cart);
    const codeSum = codeRows.reduce((a, c) => a + parseFloat(c.discount_amount), 0);

    const checks = [
      {
        check: 'Only discounted products in _offer_data',
        result: payload.length === discountedLines ? 'PASS' : 'FAIL',
        detail: `${payload.length} entries, ${discountedLines} discounted line(s)`,
      },
      {
        check: 'Offers + discount codes equal the cart total',
        result: Math.round((sum + codeSum) * 100) === cart.total_discount ? 'PASS' : 'FAIL',
        detail:
          `offers ${sum.toFixed(2)} + codes ${codeSum.toFixed(2)} = ` +
          `${(sum + codeSum).toFixed(2)} vs ${(cart.total_discount / 100).toFixed(2)}`,
      },
      {
        check: 'Every entry has a SKU',
        result: payload.every((p) => p.sku !== 'UNKNOWN') ? 'PASS' : 'FAIL',
        detail: payload.filter((p) => p.sku === 'UNKNOWN').map((p) => p.name).join(', ') || 'all present',
      },
      {
        check: 'free + paid equals quantity',
        result: payload.every((p) => p.free_quantity + p.paid_quantity === p.quantity)
          ? 'PASS'
          : 'FAIL',
        detail: '',
      },
      {
        check: 'No legacy _offer_N keys on cart',
        result: Object.keys(attrs).some((k) => /^_offer_\d/.test(k) && attrs[k]) ? 'FAIL' : 'PASS',
        detail: Object.keys(attrs).filter((k) => /^_offer_\d/.test(k) && attrs[k]).join(', ') || 'clean',
      },
      {
        check: 'Offer name resolved on every entry',
        result: payload.every((p) => p.offer_name) ? 'PASS' : 'PARTIAL',
        detail: payload.filter((p) => !p.offer_name).map((p) => p.sku).join(', ') || 'all resolved',
      },
    ];

    console.table(checks);
    console.log('=== _offer_data ===');
    console.table(payload);
    console.log('=== NOTE ===\n' + this.buildDiscountNote(this.mergePayloadBySku(payload, rows, cart), this.extractCodeData(cart)));

    return { checks, payload };
  }

  /* ==================================================================
   * UTILITIES
   * ================================================================ */

  log(...args) {
    if (this.config.debug) console.log('[DiscountNoteSync]', ...args);
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
    if (this.config.debugCartRequests) this.attachCartDebugLogging();
    this.log('Config updated');
  }

  setEnabled(enabled) {
    this.config.enabled = enabled;
  }

  getState() {
    return {
      lastKnownNote: this.state.lastKnownNote,
      lastPayload: this.state.lastPayload,
      offerTagCache: this.state.offerTagCache,
      config: this.config,
    };
  }

  async manualSync() {
    this.log('Manual sync triggered');
    this.state.lastKnownNote = null;
    await this.syncNote();
  }
}

DiscountNoteSync.VERSION =
  'v10.5.3 (2026-11-03) - split cart lines merged per SKU (order #1114864)';

if (typeof module !== 'undefined' && module.exports) {
  module.exports = DiscountNoteSync;
}

if (document.readyState !== 'loading') {
  window.discountNoteSync = new DiscountNoteSync({ debug: true });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    window.discountNoteSync = new DiscountNoteSync({ debug: true });
  });
}